(ns app.ui.reports.report
  (:require ["/js/utils/currency"                         :as currency]
            ["jquery"                                     :as jquerying]
            [app.dev                                      :as dev]
            [app.release                                  :refer [bust-cache]]
            [app.routes                                   :as r]
            [app.rpc                                      :as rpc]
            [app.transit                                  :as transit]
            [app.ui.reports.common                        :as comm]
            [app.ui.version                               :as version]
            [castra.core]
            ["clipboard"                                  :as clipboard]
            ["highcharts"]
            [clojure.string                               :as str]
            [geir.constants                               :as const]
            [geir.timezones                               :as timezones]
            [hoplon.core
             :refer [div thead tbody tr td th a i div
                     span img h1 h2 h3 h5 option form em ul li p defelem if-tpl
                     cond-tpl do-watch for-tpl with-timeout when-tpl text]
             :as h]
            [hoplon.storage-atom
             :refer [local-storage]]
            [javelin.core
             :refer [defc defc= cell cell= formula-of with-let cell-let dosync
                     formulet]]
            [medley.core                                  :as medley]
            [app.ui.campaigns.flights.site-zone-typeahead :as szt]
            [ui.component                                 :as component]
            [ui.button                                    :as button]
            [ui.form                                      :as form]
            [ui.form.input                                :as input]
            [ui.grid                                      :as grid]
            [ui.helix-hoplon                              :as helix-hoplon]
            [ui.highcharts                                :as highcharts]
            [ui.icon                                      :as icon]
            [ui.paging                                    :as p]
            [ui.util                                      :as util]
            [app.ui.reports.new-table                     :as new-table]
            [clojure.set :as set]
            [cemerick.url :as url]
            [devtools.core                                :as devtools]
            [app.crypto :as crypto]
            [helix.core                                   :as helix]
            ["@mui/material"                              :refer [Tooltip]]
            ["@mui/icons-material"                        :refer [Help]])
  (:require-macros [ui.macros            :refer [with-attr-bindings]]
                   [ui.screen            :as    screen]
                   [ui.util.experimental :refer [with-dims with-time]]))

;; (devtools/install!)

(def ^:const DEFAULT-FILTER-RPC-PAGE-SIZE 50)

(def ^:const device-group-bys #{"formFactor" "brandName" "modelName" "advertisedOs" "osMajorVersion" "osMinorVersion" "browser" "browserMajorVersion" "browserMinorVersion"})
(def ^:const most-common-device-group-bys #{"advertisedOs" "formFactor" "brandName"})
(def ^:const bid-modifier-group-bys #{"bidModifierType" "bidModifierValue" "bidModifierMultiplier" "bidModifierPrice"})
(def ^:const most-common-bid-modifier-group-bys #{"bidModifierType" "bidModifierValue" "bidModifierMultiplier" "bidModifierPrice"})
(def ^:const bid-modifier-text-filters #{"bidModifierValue"})
(def ^:const bid-modifier-numeric-filters #{"bidModifierMultiplier" "bidModifierPrice"})
(def ^:const placement-category-text-filters #{"placementCategory" "matchedPlacementCategory"})
(def ^:const search-term-text-filters #{"searchTerm" "matchedSearchTerm"})

;; placementCategory can be used in both inventory and performance reports.
;; If it is present without other performance dimensions, we run an inventory report.
(def ^:const inventory-group-bys #{"adTypeId" "city" "countryCode" "metroCode" "placementCategory" "siteId" "zoneId" "regionCode"})
(def ^:const performance-group-bys #{"advertiserId" "adId" "bidModifier" "campaignId" "channelId" "creativeId" "device" "flightId" "keyword" "matchedPlacementCategory" "matchedSearchTerm" "priorityId" "product" "deprecatedProduct" "searchTerm" "walletId"})


(def ^:const product-group-bys
  (clojure.set/union
   comm/promoted-product-group-bys
   comm/purchased-product-group-bys))

(def ^:const deprecated-product-group-bys
  ;; DEPRECATED product dimensions
  #{"productSkuId" "productMainCategoryId" "productBrandId" "productMerchantId"})

(def ^:const product-text-filters
  #{;; DEPRECATED
    "productSkuName" "productBrandName" "productMainCategoryName" "productMerchantName"
                                    ;; New disambiguated
    "promotedProductSkuName" "promotedProductBrandName" "promotedProductMainCategoryName" "promotedProductMerchantName"
    "purchasedProductSkuName" "purchasedProductBrandName" "purchasedProductMainCategoryName" "purchasedProductMerchantName"})

(def ^:const most-common-product-group-bys #{"promotedProductSkuId" "promotedProductMainCategoryId" "purchasedProductSkuId" "purchasedProductMainCategoryId"})
(def ^:const most-common-deprecated-product-group-bys #{"productSkuId" "productMainCategoryId" "productBrandId" "productMerchantId"})

(defc window-size [(-> js/window jquerying .width)
                   (-> js/window jquerying .height)])

(-> js/window
    jquerying
    (.resize #(reset! window-size
                      [(-> js/window jquerying .width)
                       (-> js/window jquerying .height)])))

(def state (cell :start))

(defmulti event!* (fn [current-state event-name & _] [current-state event-name]))

(defn event!
  [event-name & args]
  (let [s      @state
        ;; _      (.debug js/console "In state" (pr-str s) ", received event" (pr-str event-name))
        result (apply event!* s event-name args)]
        ;; _      (.debug js/console "Resulting state" (pr-str @state))]
    result))

(defc= report-date-iso-format-enabled
  (boolean (get rpc/feature-flags :isReportDateIsoFormat)))

;;; Page state

(defc report-id nil)
(defc report-status ::unknown)
(defc report-data nil)
(defc report-meta nil)
(defc report-scheduled nil)
(defc report-type nil)
(defc report-submit-failed? false)
(defc report-id-to-delete nil)
(defc error-message nil)
(defc error nil)
(defc copying? false)
(defc scheduling? false)
(defc scheduled-report-editing? false)

(def form-cells
  (atom {:start-date        (cell nil)                        ;; we need a separate cell for datetime and date
         :start-datetime    (cell nil)                        ;; because DateTimePicker element applies UTC offset to the state kept in this cell,
         :end-date          (cell nil)                        ;; which caused several subtle bugs when switching between Hourly and non-hourly reports when this cell was shared with DatePicker
         :end-datetime      (cell nil)                        ;; it's much easier to allow them to keep store their states separately
         :confirm-future    (cell nil)
         :date-group        (cell nil)
         :timezone          (cell nil)
         :date-format       (cell nil)
         :group-bys         (cell nil)
         :date-range        (cell nil)
         :report-name       (cell nil)
         :kickoffdate       (cell nil)
         :repeats           (cell nil)
         :window            (cell nil)
         :other-emails      (cell nil)
         :fixed-date        (cell nil)
         :show-revenue      (cell nil)
         :show-gmv          (cell nil)
         :show-ivt          (cell nil)
         :show-conversions  (cell nil)
         :show-events       (cell nil)
         :show-bucketing    (cell nil)
         ;; Set of vectors like [name {:id id :__title__ title}] where name is a
         ;; string, id is a number or string, and title is a string.
         :filters           (cell nil)
         :exclusion-filters (cell nil)}))

(defn reset-form
  ;; reset form needs to be independent from (reset! report-meta nil)
  ;; as sometimes we want to 'revert to previous'
  ([] (reset-form @form-cells))
  ([form-cells-state]
   (let [req (get @report-meta "Request")
         schedule (get req "Schedule")
         filter-options (fn [req filter-group]
                          (into #{}
                                (map
                                  (fn [{:keys [field value title] :as filter}]
                                      [(or field (key (first filter)))
                                       {:id        (or value (val (first filter)))
                                        :__title__ (or title (val (first filter)))}]))
                                (get req filter-group)))]

        (dosync
             ;; reset all the queuing features
             (reset! (:confirm-future form-cells-state) false)
             (reset! (:date-group form-cells-state)
                     (or
                       (get req "TimePeriod")
                       "total"))
             (reset! (:start-date form-cells-state)
                     (or
                       (when-let [start (get req "Start")]
                                 (-> (js/moment. start) .toDate))
                       (util/days-ago 30)))
             (reset! (:start-datetime form-cells-state)
                     (or
                       (when-let [start (get req "Start")]
                              (util/as-utc start))
                       (util/days-ago (util/utc-now) 30)))
             (reset! (:end-date form-cells-state)
                     (or
                        ;; we need to subtract 1 day to handle the fact that reporting backend treats  End date as exclusive
                        ;; so before sending report request we added 1 day and we now need to subtract it, not to confuse the user
                        (let [days (if schedule 0 1)]
                             (when-let [end (get req "End")]
                                       (-> (js/moment. end)
                                           (.subtract days "days")
                                           .toDate)))
                        (util/start-of-day)))
             (reset! (:end-datetime form-cells-state)
                     (or
                       ;; we need to subtract 1 hour, to handle the fact that reporting backend treats End date as exclusive
                       ;; so before sending report request we added 1 hour and we now need to subtract it, not to confuse the user
                       (let [hours (if schedule 0 1)]
                            (when-let [end (get req "End")]
                                      (-> js/moment
                                          (.utc end)
                                          (.subtract hours "hours")
                                          .toDate)))
                       (util/start-of-day (util/utc-now))))
             (reset! (:timezone form-cells-state) (get req "TimeZone" "UTC"))
             (reset! (:date-format form-cells-state) (or
                                                      (get-in schedule ["Criteria" "ReportDateFormat"])
                                                      (get req "ReportDateFormat")
                                                      (if @report-date-iso-format-enabled "iso8601" "us")))

             (reset! (:filters form-cells-state)
                     (if (get-in schedule ["Criteria" "Parameters"])
                       (filter-options (get schedule "Criteria") "Parameters")
                       (filter-options req "Parameters")))

             (reset! (:exclusion-filters form-cells-state)
                     (if (get-in schedule ["Criteria" "ExcludeParameters"])
                       (filter-options (get schedule "Criteria") "ExcludeParameters")
                       (filter-options req "ExcludeParameters")))

             (reset! (:group-bys form-cells-state)
                     (if (get-in schedule ["Criteria" "GroupBy"])
                       (apply sorted-set (get-in schedule ["Criteria" "GroupBy"]))
                       (apply sorted-set (get req "GroupBy"))))
             (reset! (:date-range form-cells-state)
                     (or
                       (get schedule "ToDate")
                       (get req "ToDate")
                       const/REPORT-TODATE-CUSTOM))
             ;; reset all the scheduling features
             (reset! (:report-name form-cells-state)
                     (get schedule "Name" ""))
             (reset! (:kickoffdate form-cells-state)
                     (or
                       (some-> (get schedule "KickoffDate") js/moment. .toDate)
                       nil))
             (reset! (:repeats form-cells-state)
                     (get schedule "RecurrenceType" const/REPORT-REPEAT-DAILY))
             (reset! (:window form-cells-state)
                     (get schedule "SchedulingWindow" const/REPORT-RUN-FIRST))
             (reset! (:other-emails form-cells-state)
                     (get schedule "Emails" nil))
             (reset! (:fixed-date form-cells-state)
                     (get schedule "FixedDate" false))
             (reset! (:show-revenue form-cells-state)
                     (get schedule "ShowRevenue" true))
             (reset! (:show-gmv form-cells-state)
                     (get schedule "ShowGMV" true))
             (reset! (:show-ivt form-cells-state)
                     (get schedule "ShowIVT" true))
             (reset! (:show-conversions form-cells-state)
                     (get schedule "ShowConversions" true))
             (reset! (:show-events form-cells-state)
                     (get schedule "ShowEvents" true))
             (reset! (:show-bucketing form-cells-state)
                     (get schedule "ShowClickBucketing" true)))

           ;; return all initialised state
        form-cells-state)))

;;; Web Worker

(def worker-calls (atom {}))

(def worker
  (js/Worker. (bust-cache "/js/report-worker.js")))

(defn handle-worker-message
  [msg]
 ;(.log js/console "Received message from worker" msg)
  ;; (.log js/console "worker-calls" @worker-calls)
  (let [decoded                             (try
                                              (-> msg .-data transit/decode)
                                              (catch :default _
                                                nil))
        {:keys [result call-id data]} decoded
        {:keys [complete error]}            (get @worker-calls call-id)]
    (cond
      ;; This is here because it seems that Papa sends messages
      ;; containing the parsed result, even though we didn't ask
      ;; for them.
      (nil? call-id)
      (prn "Ignoring improperly formatted worker message.")

      (= result :success)
      (complete data)

      :else
      (error error))
    ;; TODO: Consider deregistering conditionally based on return
    ;; value of complete. That would allow for multiple responses
    ;; for a given request.
    (swap! worker-calls dissoc call-id)))

(-> worker
    .-onmessage
    (set! handle-worker-message))

(defn worker-call
  "Invokes an operation on the worker and watches for the response.
  Calls the `complete` callback when a response is received."
  [op data complete error]
  (let [call-id (str (gensym))]
    (swap! worker-calls assoc call-id {:complete complete
                                       :error    error})
    (.postMessage worker (transit/encode {:op      op
                                          :call-id call-id
                                          :data    data}))))

;;; RPC/Remote calls

;; TODO: All of this needs to deal with errors better - when a report
;; meta retrieval fails, when report retrieval fails, etc. etc.

(def fetch-status
  ;; By specifying our own loading cell, we prevent the "loading"
  ;; message from flashing at the top of the screen
  (let [loading (cell nil)]
    ;; We don't want to fetch status if there's already a call
    ;; outstanding to do so.
    (if-not (empty? @loading)
      (prn "Skipping status fetch: call is outstanding")
      (rpc/remote 'report-status
                  :state (util/trigger #(event! :status-received %))
                  :error (util/trigger #(when % (event! :status-fetch-error %)))
                  :loading loading))))


(def fetch-scheduled-report
  (rpc/remote 'report-scheduled
              :state (util/trigger #(event! :meta-downloaded-scheduling %))
              :error (util/trigger #(when % (event! :meta-fetch-error %)))
              ;; By specifying our own loading cell, we prevent
              ;; the "loading" message from flashing at the top of the
              ;; screen
              :loading (cell nil)))

(def fetch-meta
  (rpc/remote 'report-meta
              :state (util/trigger #(event! :meta-downloaded %))
              ;; Probably not a good idea to share the error cell
              :error (util/trigger #(when % (event! :meta-fetch-error %)))
              ;; By specifying our own loading cell, we prevent
              ;; the "loading" message from flashing at the top of the
              ;; screen
              :loading (cell nil)))

(declare compute-report-data)

(def submit-request-json!
  (fn []
    (let [next-state    (if @scheduling? :report-scheduled :report-submitted)
          endpoint      (if @scheduling? 'report-schedule 'report-submit)]
      (rpc/remote endpoint
                  :state (util/trigger #(event! next-state))
                  :error (util/trigger #(when % (event! :report-submit-failed %)))))))

;;; Page State Transitions

(defmethod event!* [:start :page-entered]
  [_ _ & [id]]
  (dosync
   (reset! state :checking-status)
   (fetch-status id)
   (reset! report-id id)))

(defmethod event!* [:new-report-id :page-entered]
  [_ _ & [id]]
  (dosync
   (reset! report-id id)
   (reset! state :edit-request)))

(defmethod event!* [:checking-status :status-received]
  [_ _ & [status]]
  (if status
    ;; If there is a status, then the report exists
    (do
      (reset! state :awaiting-report)
      (event! :status-received status))
    ;; If there is not a status, the report doesn't exist, and we should show
    ;; the 'edit request' UI.
    (reset! state :edit-request)))

(defmethod event!* [:edit-request :submit-request]
  [_ _ & [request-json]]
  (reset! state :report-submitting)
  ((submit-request-json!) request-json))

(defmethod event!* [:scheduled-report-editing :submit-request]
  [_ _ & [request-json]]
  (reset! state :report-submitting)
  ((submit-request-json!) request-json))

(def report-status-check-interval 2000)

(defmethod event!* [:report-submitting :report-scheduled]
  [_ _]
  (if @scheduled-report-editing?
    (event! :report-deleting)
    (event! :report-submitted)))

(defmethod event!* [:report-submitting :report-deleting]
  [_ _]
  ;; delete the report
  ((rpc/remote 'delete-scheduled-report!
               :state (util/trigger #(event! :report-submitted))
               :error (util/trigger #(when % (event! :report-submit-failed %))))
   {:id @report-id-to-delete}))

(defmethod event!* [:report-submitting :report-submitted]
  [_ _]
  ;; redirect to scheduled-reports if successfully edited or keep polling for results if custom report
  (if (or @scheduling? @scheduled-report-editing?)
     (dosync
       (reset! state :start)
       (set! js/window.location.hash (str "#!/" @rpc/networkid "/scheduled-reports/")))
     (dosync
       (reset! state :awaiting-report)
       (reset! report-submit-failed? false)
       (with-timeout report-status-check-interval (fetch-status @report-id)))))

(defmethod event!* [:report-submitting :report-submit-failed]
  [_ _ & [args]]
  (let [error-data    (ex-data args)]
    (dosync
     (reset! state :edit-request)
     ;; FIXME: The rest of the code base indicates validation error by setting
     ;; `:error-code` (not `:reason`) to `:validation-error.
     ;;
     ;; TODO:
     ;; * Ensure that `:error-code :validation-error` is coming through
     ;;   appropriately.
     ;; * Use `:error-code` here instead of `:reason`.
     (if (= (:reason error-data) :validation-error)
       (reset! error-message (:detail error-data))
       (reset! error-message nil))
     (reset! report-submit-failed? true))))

(defmethod event!* [:awaiting-report :status-received]
  [_ _ & [{:strs [status] :as received}]]
  (reset! report-status received)
  (condp contains? status
    #{const/REPORT-STATUS-SUBMITTED const/REPORT-STATUS-IN-PROGRESS}
    (with-timeout report-status-check-interval (fetch-status @report-id))
    #{const/REPORT-STATUS-DONE}
    (event! :report-available)
    #{const/REPORT-STATUS-ERROR}
    (event! :report-error)
    #{const/REPORT-STATUS-TOO-LARGE}
    (event! :report-too-large)))

(def status-retrieval-error
  (rpc/from-lang-pack ::event-report-status-retrieval-error))

(defmethod event!* [:awaiting-report :status-fetch-error]
  [_ _]
  (dosync
   (reset! state :error)
   (reset! error {:detail @status-retrieval-error})))

(defmethod event!* [:checking-status :status-fetch-error]
  [_ _]
  (dosync
   (reset! state :error)
   (reset! error {:detail @status-retrieval-error})))

(defmethod event!* [:awaiting-report :report-available]
  [_ _]
  (reset! state :downloading-meta)
  (fetch-meta @report-id))

(declare edit-report)

(defmethod event!* [:awaiting-report :report-too-large]
  [_ _]
  (reset! state :report-too-large)
  (fetch-meta @report-id))

(def no-matching-data-detail
  (rpc/from-lang-pack ::event-report-computed-error-detail))

(def no-matching-data-remediation
  (rpc/from-lang-pack ::event-report-computed-error-remediation))

(def no-matching-data-button
  (rpc/from-lang-pack ::event-report-computed-error-remediation-button))

(defmethod event!* [:computing-in-worker :report-computed]
  [_ _ & [data]]
  (if (empty? (:rollup-rows data))
    (dosync
      (reset! state :error)
      (reset!
        error
        {:heading ::event-error-header-notice
         :heading-text ::event-report-computed-error-header-text
         :detail
         @no-matching-data-detail

         :remediation
         (div
           (div @no-matching-data-remediation)
           (button/primary
             :class "report-error-button"
             :click #(set! js/window.location.hash (str "#!/" @rpc/networkid "/reports/new"))
             :text  @no-matching-data-button))}))
    (dosync
      (reset! report-data data)
      (reset! state :displaying-report))))

(declare download-csv)

(def report-too-big-detail
  (rpc/from-lang-pack ::event-report-too-big-error-detail))

(def report-too-big-remediation1
  (rpc/from-lang-pack ::event-report-too-big-error-remediation1))

(def report-too-big-remediation2
  (rpc/from-lang-pack ::event-report-too-big-error-remediation2))

(def report-too-big-download
  (rpc/from-lang-pack ::event-report-too-big-error-button-download))

(defmethod event!* [:computing-in-worker :report-too-big]
  [_ _]
  (dosync
   ;; Typically this gets set after the worker process the data.
   ;; But we still want the meta to show up even though we won't be
   ;; processing any of the report.
   (reset! report-data {:report-meta @report-meta})
   (reset! state :report-too-big)))

(def csv-retrieval-error
  (rpc/from-lang-pack ::event-report-csv-retrieval-error))

(defmethod event!* [:computing-in-worker :report-csv-retrieval-error]
  [_ _]
  (dosync
   (reset! state :error)
   (reset! error {:detail @csv-retrieval-error})))

(defmethod event!* [:downloading-meta :meta-downloaded]
  [_ _ & [meta]]
  (dosync
   (reset! report-meta meta)
   (reset! state :computing-in-worker)
   (compute-report-data)))

(defmethod event!* [:report-too-large :meta-downloaded]
  [_ _ & [meta]]
  (dosync
   (reset! state :error)
   (reset! error {:detail (div :text (rpc/from-lang-pack
                                      ::new-report-form-error-report-too-large))
                  :remediation (div
                                (div :text (rpc/from-lang-pack
                                            ::new-report-form-error-report-too-large-remediation))
                                (button/primary
                                 :class "report-error-button"
                                 :click edit-report
                                 :text  (rpc/from-lang-pack
                                         ::report-summary-button-edit-text)))})
   (reset! report-meta meta)))

(defmethod event!* [:edit-request :meta-downloaded-scheduling]
  [_ _ & [report-scheduled]]
  (dosync
    (reset! report-meta report-scheduled)
    (reset-form @form-cells)
    (reset! state :scheduled-report-editing)))

(def meta-fetch-error
  (rpc/from-lang-pack ::event-report-meta-fetch-error))

(defmethod event!* [:downloading-meta :meta-fetch-error]
  [_ _]
  (dosync
   (reset! state :error)
   (reset! error {:detail @meta-fetch-error})))

(defmethod event!* [:report-too-large :meta-fetch-error]
  [_ _]
  (dosync
   (reset! state :error)
   (reset! error {:detail @meta-fetch-error})))

(defmethod event!* :default
  [state event]
  (do
    (.warn js/console "Unknown state / event type combo:"
           (clj->js [(:state state) (:type event)]))
    state))

;;; Report Schema

(defc= request-schema
  {"adTypeId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-adtypeid-title)
    :enabled?    true
    :groupable?  true
    :filterable? true
    :filter-rpc  (p/paginate
                  (rpc/remote 'adtypes)
                  :page-size DEFAULT-FILTER-RPC-PAGE-SIZE)
    :inventory? true}

   "advertiserId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-advertiserid-title)
    :enabled?    true
    :groupable?  true
    :filterable? true
    :filter-rpc  (p/paginate
                  (rpc/remote 'advertisers)
                  :page-size DEFAULT-FILTER-RPC-PAGE-SIZE)
    :performance? true}

   "campaignId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-campaignid-title)
    :enabled?    true
    :groupable?  true
    :filterable? true
    :filter-rpc  (p/paginate
                  (rpc/remote 'campaigns)
                  :page-size DEFAULT-FILTER-RPC-PAGE-SIZE)
    :performance? true}

   "walletId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-walletid-title)
    :enabled?    true
    :groupable?  true
    :filterable? true
    :filter-rpc  (p/paginate
                  (rpc/remote 'wallets)
                  :page-size DEFAULT-FILTER-RPC-PAGE-SIZE)
    :performance? true}

   "channelId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-channelid-title)
    :enabled?    true
    :groupable?  true
    :filterable? true
    :filter-rpc  (p/paginate
                  (rpc/remote 'channels)
                  :page-size DEFAULT-FILTER-RPC-PAGE-SIZE)
    :performance? true}

   "countryCode"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-countrycode-title)
    :enabled?    true
    :groupable?  true
    :filterable? true
    :filter-rpc  (p/paginate
                  (rpc/remote 'geo-countries)
                  :page-size DEFAULT-FILTER-RPC-PAGE-SIZE)
    :inventory?  true}

   "creativeId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-creativeid-title)
    :enabled?    true
    :groupable?  true
    :filterable? true
    :filter-rpc  (p/paginate
                  (rpc/remote 'creatives-by-title)
                  :page-size DEFAULT-FILTER-RPC-PAGE-SIZE)
    :performance? true}
   
   "adId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-adid-title)
    :enabled?    true
    :groupable?  true
    :filterable? false
    :performance? true}


   "flightId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-flightid-title)
    :enabled?    true
    :groupable?  true
    :filterable? true
    :filter-rpc  (p/paginate
                  (rpc/remote 'flights)
                  :page-size DEFAULT-FILTER-RPC-PAGE-SIZE)
    :performance? true}

   "price"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-price-title)
    :enabled?    true
    :groupable?  false
    :filterable? false
    :performance? true}

   "keyword"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-keyword-title)
    :enabled?    true
    :groupable?  true
    :filterable? true
    :performance? true}

   "metroCode"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-metrocode-title)
    :enabled?    true
    :groupable?  true
    :filterable? true
    :filter-rpc  (p/paginate
                  (rpc/remote 'geo-metros)
                  :page-size DEFAULT-FILTER-RPC-PAGE-SIZE)
    :inventory?  true}

   "priorityId"
   {:title               ~(rpc/from-lang-pack
                           ::request-schema-field-priorityid-title)
    :enabled?            true
    :display-fn          #(cell= (str (:__title__ %)
                                      " ("
                                      (:channeltitle %) ")"))
    :selected-display-fn #(str (:__title__ %) " (" (:channeltitle %) ")")
    :groupable?          true
    :filterable?         true
    :filter-rpc          (p/paginate
                          (rpc/remote 'priorities)
                          :page-size DEFAULT-FILTER-RPC-PAGE-SIZE)
    :performance? true}

   "siteId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-siteid-title)
    :enabled?    true
    :groupable?  true
    :filterable? true
    :filter-rpc  (p/paginate
                  (rpc/remote 'sites)
                  :page-size DEFAULT-FILTER-RPC-PAGE-SIZE)
    :inventory?  true}

   "placementCategory"
   {:title        ~(rpc/from-lang-pack ::request-schema-field-placementcategory-title)
    :enabled?     ~(cell= (:reportingPlacementCategory rpc/feature-flags))
    :groupable?   true
    :filterable?  true
    :inventory?   true}

   "matchedPlacementCategory"
   {:title        ~(rpc/from-lang-pack ::request-schema-field-matchedplacementcategory-title)
    :enabled?     ~(cell= (:reportingPlacementCategory rpc/feature-flags))
    :groupable?   true
    :filterable?  true
    :performance? true}

   "searchTerm"
   {:title        ~(rpc/from-lang-pack ::request-schema-field-searchterm-title)
    :enabled?     ~(cell= (:reportingSearchTerms rpc/feature-flags))
    :groupable?   true
    :filterable?  true
    :performance? true}

   "matchedSearchTerm"
   {:title        ~(rpc/from-lang-pack ::request-schema-field-matchedsearchterm-title)
    :enabled?     ~(cell= (:reportingSearchTerms rpc/feature-flags))
    :groupable?   true
    :filterable?  true
    :performance? true}

   "zoneId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-zoneid-title)
    :enabled?    true
    :groupable?  true
    :filterable? true
    :results-fn  (fn [{:keys [exclusion-filters?]}]
                   (szt/zone-results
                     ;; NOTE: `:all` is not a special value that
                     ;; `szt/zone-results` recognizes. At the moment, the business
                     ;; logic simply considers whether or not this value is
                     ;; truthy.
                    {:site-id                           :all
                     :include-no-zone-targeting-option? exclusion-filters?}))
    :filter-rpc  (p/paginate
                  (rpc/remote 'all-zones)
                  :page-size rpc/MAX-ZONES)
    :inventory?  true}

   "city"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-city-title)
    :enabled?    true
    :groupable?  true
    :filterable? true
    :inventory?  true}

   "regionCode"
   {:title               ~(rpc/from-lang-pack
                           ::request-schema-field-regioncode-title)
    :enabled?            true
    :display-fn          #(cell= (str (:__title__ %) ", " (:countryname %)))
    :selected-display-fn #(str (:__title__ %) ", " (:countryname %))
    :groupable?          true
    :filterable?         true
    :filter-rpc          (p/paginate
                          (rpc/remote 'geo-regions)
                          :page-size DEFAULT-FILTER-RPC-PAGE-SIZE)
    :inventory?          true}

   "device"
   {:title       ~(rpc/from-lang-pack
                   ::request-schema-field-device-title)
    :enabled?    ~(cell= (:queryDevice rpc/feature-flags))
    :groupable?  true
    :filterable? false
    :performance? true}

   "formFactor"
   {:title       ~(rpc/from-lang-pack
                   ::request-schema-field-formFactor-title)
    :filter-only-enabled? ~(cell= (:queryDevice rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-formfactor-title)}

   "brandName"
   {:title       ~(rpc/from-lang-pack
                   ::request-schema-field-brandName-title)
    :filter-only-enabled? ~(cell= (:queryDevice rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-brandname-title)}

   "modelName"
   {:title       ~(rpc/from-lang-pack
                   ::request-schema-field-modelName-title)
    :filter-only-enabled? ~(cell= (:queryDevice rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-modelname-title)}

   "advertisedOs"
   {:title       ~(rpc/from-lang-pack
                   ::request-schema-field-advertisedOs-title)
    :filter-only-enabled? ~(cell= (:queryDevice rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-advertisedos-title)}

   "osMajorVersion"
   {:title       ~(rpc/from-lang-pack
                   ::request-schema-field-osMajorVersion-title)
    :filter-only-enabled? ~(cell= (:queryDevice rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-osmajorversion-title)}

   "osMinorVersion"
   {:title       ~(rpc/from-lang-pack
                   ::request-schema-field-osMinorVersion-title)

    :filter-only-enabled? ~(cell= (:queryDevice rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-osminorversion-title)}

   "browser"
   {:title       ~(rpc/from-lang-pack
                   ::request-schema-field-browser-title)
    :filter-only-enabled? ~(cell= (:queryDevice rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-browser-title)}

   "browserMajorVersion"
   {:title       ~(rpc/from-lang-pack
                   ::request-schema-field-browserMajorVersion-title)
    :filter-only-enabled? ~(cell= (:queryDevice rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-browsermajorversion-title)}

   "browserMinorVersion"
   {:title       ~(rpc/from-lang-pack
                   ::request-schema-field-browserMinorVersion-title)
    :filter-only-enabled? ~(cell= (:queryDevice rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-browserminorversion-title)}

   "bidModifier"
   {:title       ~(rpc/from-lang-pack
                   ::request-schema-field-bidModifier-title)
    :enabled?    ~(cell= (:queryBidModifier rpc/feature-flags))
    :groupable?  true
    :filterable? false
    :performance? true}

   "bidModifierType"
   {:title       ~(rpc/from-lang-pack
                   ::request-schema-field-bidModifierType-title)
    :filter-only-enabled? ~(cell= (:queryBidModifier rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-bidmodifiertype-title)
    ;; Combination order from https://github.com/adzerk/odin-batch/blob/3ab9d54f1fa01e704d937fff5297aec4fd298a83/src/main/scala/odin/Events.scala#L1217
    :filter-values [{:id "keyword"           :__title__ "Keyword"}
                    {:id "site"              :__title__ "Site"}
                    {:id "zone"              :__title__ "Zone"}
                    {:id "keyword+site"      :__title__ "Keyword && Site"}
                    {:id "keyword+zone"      :__title__ "Keyword && Zone"}
                    {:id "site+zone"         :__title__ "Site && Zone"}
                    {:id "keyword+site+zone" :__title__ "Keyword && Site && Zone"}]
  }

   "bidModifierValue"
   {:title       ~(rpc/from-lang-pack
                   ::request-schema-field-bidModifierValue-title)
    :filter-only-enabled? ~(cell= (:queryBidModifier rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-bidmodifiervalue-title)}

   "bidModifierMultiplier"
   {:title       ~(rpc/from-lang-pack
                   ::request-schema-field-bidModifierMultiplier-title)
    :filter-only-enabled? ~(cell= (:queryBidModifier rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-bidmodifiermultiplier-title)}

   "bidModifierPrice"
   {:title       ~(rpc/from-lang-pack
                   ::request-schema-field-bidModifierPrice-title)
    :filter-only-enabled? ~(cell= (:queryBidModifier rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-bidmodifierprice-title)}

   "product"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-product-title)
    :enabled?    ~(cell= (:isRetailNetwork rpc/feature-flags))
    :groupable?  true
    :filterable? false
    :performance? true}

   ;; DEPRECATED Product dimensions
   "deprecatedProduct"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-product-deprecated-title)
    :enabled?    ~(cell= (:isRetailNetwork rpc/feature-flags))
    :groupable?  true
    :filterable? false
    :performance? true}

   "productSkuId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-productskuid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-productskuid-title)}

   "productSkuName"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-productskuid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  false
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-productskuname-title)}

   "productMainCategoryId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-productmaincategoryid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-productmaincategoryid-title)}

   "productMainCategoryName"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-productmaincategoryid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  false
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-productmaincategoryname-title)}

   "productBrandId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-productbrandid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-productbrandid-title)}

   "productBrandName"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-productbrandid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  false
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-productbrandname-title)}

   "productMerchantId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-productmerchantid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-productmerchantid-title)}

   "productMerchantName"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-productmerchantid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  false
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-productmerchantname-title)}

   ;; New disambiguated product dimensions
   ;;
   ;; Promoted product dimensions
   "promotedProductSkuId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-promotedproductskuid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-promotedproductskuid-title)}

   "promotedProductSkuName"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-promotedproductskuid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  false
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-promotedproductskuname-title)}

   "promotedProductMainCategoryId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-promotedproductmaincategoryid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-promotedproductmaincategoryid-title)}

   "promotedProductMainCategoryName"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-promotedproductmaincategoryid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  false
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-promotedproductmaincategoryname-title)}

   "promotedProductBrandId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-promotedproductbrandid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-promotedproductbrandid-title)}

   "promotedProductBrandName"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-promotedproductbrandid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  false
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-promotedproductbrandname-title)}

   "promotedProductMerchantId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-promotedproductmerchantid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-promotedproductmerchantid-title)}

   "promotedProductMerchantName"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-promotedproductmerchantid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  false
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-purchasedproductmerchantname-title)}

   ;; Purchased product dimensions
   "purchasedProductSkuId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-purchasedproductskuid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-purchasedproductskuid-title)}

   "purchasedProductSkuName"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-purchasedproductskuid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  false
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-purchasedproductskuname-title)}

   "purchasedProductMainCategoryId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-purchasedproductmaincategoryid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-purchasedproductmaincategoryid-title)}

   "purchasedProductMainCategoryName"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-purchasedproductmaincategoryid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  false
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-purchasedproductmaincategoryname-title)}

   "purchasedProductBrandId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-purchasedproductbrandid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-purchasedproductbrandid-title)}

   "purchasedProductBrandName"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-purchasedproductbrandid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  false
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-purchasedproductbrandname-title)}

   "purchasedProductMerchantId"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-purchasedproductmerchantid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  true
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-purchasedproductmerchantid-title)}

   "purchasedProductMerchantName"
   {:title       ~(rpc/from-lang-pack ::request-schema-field-purchasedproductmerchantid-title)
    :filter-only-enabled? ~(cell= (:isRetailNetwork rpc/feature-flags))
    :enabled?    false
    :groupable?  false
    :filterable? true
    :filter-title ~(rpc/from-lang-pack ::csv-specific-fields-purchasedproductmerchantname-title)}})


(defn- sort-by-key [m]
  (into (sorted-map-by (fn [key1 key2]
                         (compare (get m key1)
                                  (get m key2)))) m))

(defc= inventory-report-group-bys
  (->> (for [[field {:keys [enabled? groupable? title inventory?]}] request-schema
             :when (and enabled?
                        groupable?
                        inventory?)]
         [field title])
       (into {})
       sort-by-key))

(defc= performance-report-group-bys
  (->> (for [[field {:keys [enabled? groupable? title performance?]}] request-schema
             :when (and enabled?
                        groupable?
                        performance?)]
         [field title])
       (into {})
       sort-by-key))

(defc= report-filters
  (->> (for [[field {:keys [enabled? filterable? title filter-title filter-only-enabled?]}] request-schema
             :when (and (or enabled? filter-only-enabled?)
                        filterable?)]
         [field (or filter-title title)])
       (into {})
       sort-by-key))

(defc= report-exclusion-filters
  (->> (for [[field {:keys [enabled? filterable? title filter-title filter-only-enabled?]}] request-schema
             :when (and (or enabled? filter-only-enabled?)
                        filterable?)]
         [field (or filter-title title)])
       (into {})
       sort-by-key))

;;; UI Helpers

(defelem expander
  [attrs _]
  (let [{:keys [expanded? css]} attrs
        attrs               (dissoc attrs :expanded? :css)]
    (div
     :css (formula-of [expanded?]
            (merge css
                   {:display        "inline-block"
                    :width          0
                    :height         0
                    :border-left    "5px solid transparent"
                    :border-right   "5px    solid transparent"
                    :border-top     "7px    solid black"
                    :vertical-align "middle"
                    :transform      (str "rotate("
                                         (if expanded? 0 -90)
                                         "deg)")}))
     attrs)))

;;; New Report Submission/Edit

(defn- format-date
  "Converts a date to a string using the specified format, which
  defaults to `YYYY-MM-DDTHH:mm:ss`, the options map exposes
  `read-as-utc?` flag that specifies if the date should be read as UTC
  (default) or should it be read as is, without any TZ conversions."
  ([date]
   (format-date date "YYYY-MM-DDTHH:mm:ss"))
  ([date fmt & [{:keys [read-as-utc?]
                 :or   {read-as-utc? true}}]]
   (if read-as-utc?
     (-> js/moment (.utc date) (.format fmt))
     (-> (js/moment. date) (.format fmt))))) ;; difference between calling moment.utc(date) instead of moment(date).utc()
                                        ; will parse the date as UTC time (what we receive from server),
                                        ; and avoid parsing it as local TimeZone and then converting it UTC, resulting in different DateTime
                                        ; more https://momentjs.com/docs/#/parsing/utc/


(defn- format-datetime
  "Converts an instant to UI displaying the date and time "
  [row field]
  (let [date (get row field)]
    (div
     :class "datetime"
     (div :class "date" (format-date date "YYYY-MM-DD"))
     (div :class "time" (str (format-date date "hh:mm A"))))))

(defn round-up-always
  "Rounds to decision to precision, but always rounds up."
  [n precision]
  ;; Multiplying by 10^precison allows us to shift the digits.
  ;; Then we divide by the same to move the decimal back.
  ;; So if we have 10.245 with a precision of two,
  ;; We turn it into 1024.5
  ;; Then apply ceil to make it 1025
  ;; The divide to get back 10.25
  (/ (Math/ceil (* (Math/pow 10 precision) n))
     (Math/pow 10 precision)))

(defn format-currency
  "Formats a number as a money amount. Rounds positive values to the
 currency's smallest displayed unit (the cent for USD) in the direction
 of positive infinity. Rounds negative values in the direction of
 negative infinity. Display only: the amount is never converted.

 When the network sets a currency, the amount is shown in it, in the
 browser locale. When it sets none, it is shown as US dollars exactly as
 before network currencies (\"$1234.50\")."
  ([row field]
   (when-let [n (when (and (get row field)
                           (not (str/blank? (get row field))))
                  (get row field))]
     (format-currency n)))
  ([amount]
   (if-let [currency (currency/getNetworkCurrency)]
     (let [places  (currency/getCurrencyFractionDigits currency)
           ;; We use abs here to make sure "up" here
           ;; means towards negative infinity when negative.
           rounded (round-up-always (Math/abs amount) places)]
       (currency/formatCurrency (if (neg? amount) (- rounded) rounded)
                                #js {:currency              currency
                                     :minimumFractionDigits places
                                     :maximumFractionDigits places}))
     (str (if (neg? amount) "-" "")
          "$"
          (.toLocaleString (.toFixed (round-up-always
                                         ;; We use abs here to make sure "up" here
                                         ;; means towards negative infinity when negative.
                                      (Math/abs amount) 2) 2))))))

(defn format-revenue
  "Formats a revenue to a currency amount (see `format-currency`), or to nil
 if it's zero."
  ([row field]
   (let [r (get row field)]
     (when-not (zero? r)
       (format-currency row field))))
  ([amount]
   (when-not (zero? amount)
     (format-currency amount))))

(defn format-human-integer
  "Formats an integer with commas."
  [row field]
  (let [value (or (get row field) 0)]
    (.toLocaleString value)))

(defn format-percent*
  "Returns a number `n` as a percentage with `precision` decimal places."
  [n precision]
  (str (.toFixed (* n 100.0) precision) "%"))

(defn format-percent
  "Returns a formatter for a number as a percentage with the specified
  number of decimal places."
  [precision]
  (fn [row field]
    (when (and (get row field)
               (not (str/blank? (get row field))))
      (format-percent* (get row field) precision))))

(defn flatrate-formatter
  "Returns a formatter that replaces the content with 'flat_rate' if the row is a flat rate row."
  ([] (flatrate-formatter format-human-integer)) ;; format-human-integer is the default fallback formatter
  ([formatter]
  (fn [row field]
    (let [flatrateevent (get row "flatrateevent")
          is-flatrate-event? (and flatrateevent (= "true" (str/lower-case flatrateevent)))]
      (if is-flatrate-event?
        "flat_rate"
        (formatter row field))))))

(let [link-formats {"campaignid" {:prefix "campaign"
                                  :suffix "flights"}
                    "flightid"   {:prefix "flight"
                                  :suffix "edit"}
                    "advertiserid" {:prefix "advertiser"
                                    :suffix "campaigns"}}]
  (defn link-formatter
    "Returns a formatter that will render a link element for the given
  field."
    [id-field]
    (fn [row field]
      (let [{:keys [prefix suffix]} (get link-formats id-field)]
        (a :href (cell= (str "#!/" rpc/networkid "/" prefix "/" (get row id-field) "/" suffix "/"))
           (get row field))))))

(defn- format-field-value
  "Formats a field value. Note that arguments are values, not cells.
  Mode can be `:summary` to use the summary formatter."
  [row field report-field-formats-fn & [{:keys [mode]}]]
  (if (nil? (get row field))
    ""
    (let [{:keys [format summary-format]} (get (report-field-formats-fn) field)
          format                          (or (when (= mode :summary)
                                                summary-format)
                                              format
                                              format-human-integer)]
      (format row field))))


(def csv-specific-fields
  ;; Due to a bug the number of bindings have to be even. If you edit
  ;; this and run into this error you can add a _ nil or something as
  ;; a binding to get around it.
  ;; See: https://github.com/hoplon/javelin/pull/38
  (formulet [csv-specific-fields-advertisername-title             (rpc/from-lang-pack ::csv-specific-fields-advertisername-title)
             csv-specific-fields-campaignname-title               (rpc/from-lang-pack ::csv-specific-fields-campaignname-title)
             csv-specific-fields-walletid-title                   (rpc/from-lang-pack ::csv-specific-fields-walletid-title)
             csv-specific-fields-walletname-title                 (rpc/from-lang-pack ::csv-specific-fields-walletname-title)
             csv-specific-fields-walletexternalid-title           (rpc/from-lang-pack ::csv-specific-fields-walletexternalid-title)
             csv-specific-fields-walletcurrency-title             (rpc/from-lang-pack ::csv-specific-fields-walletcurrency-title)
             csv-specific-fields-clickconversions-title           (rpc/from-lang-pack ::csv-specific-fields-clickconversions-title)
             csv-specific-fields-clicks-title                     (rpc/from-lang-pack ::csv-specific-fields-clicks-title)
             csv-specific-fields-ctr-title                        (rpc/from-lang-pack ::csv-specific-fields-ctr-title)
             csv-specific-fields-duplicateimpressionsclicks-title (rpc/from-lang-pack ::csv-specific-fields-duplicateimpressionsclicks-title)
             csv-specific-fields-duplicateipclicks-title          (rpc/from-lang-pack ::csv-specific-fields-duplicateipclicks-title)
             csv-specific-fields-ecpm-title                       (rpc/from-lang-pack ::csv-specific-fields-ecpm-title)
             csv-specific-fields-cpc-title                        (rpc/from-lang-pack ::csv-specific-fields-cpc-title)
             csv-specific-fields-end-title                        (rpc/from-lang-pack ::csv-specific-fields-end-title)
             csv-specific-fields-flightname-title                 (rpc/from-lang-pack ::csv-specific-fields-flightname-title)
             csv-specific-fields-impressions-title                (rpc/from-lang-pack ::csv-specific-fields-impressions-title)
             csv-specific-fields-unfilteredimpressions-title      (rpc/from-lang-pack ::csv-specific-fields-unfilteredimpressions-title)
             csv-specific-fields-invaliduaimpressions-title       (rpc/from-lang-pack ::csv-specific-fields-invaliduaimpressions-title)
             csv-specific-fields-duplicateimpressions-title       (rpc/from-lang-pack ::csv-specific-fields-duplicateimpressions-title)
             csv-specific-fields-suspiciousimpressions-title      (rpc/from-lang-pack ::csv-specific-fields-suspiciousimpressions-title)
             csv-specific-fields-uniqueimpressions-title          (rpc/from-lang-pack ::csv-specific-fields-uniqueimpressions-title)
             csv-specific-fields-rawimpressions-title             (rpc/from-lang-pack ::csv-specific-fields-rawimpressions-title)
             csv-specific-fields-invaliduaclicks-title            (rpc/from-lang-pack ::csv-specific-fields-invaliduaclicks-title)
             csv-specific-fields-price-title                      (rpc/from-lang-pack ::csv-specific-fields-price-title)
             csv-specific-fields-ratetype-title                   (rpc/from-lang-pack ::csv-specific-fields-ratetype-title)
             csv-specific-fields-rawclicks-title                  (rpc/from-lang-pack ::csv-specific-fields-rawclicks-title)
             csv-specific-fields-revenue-title                    (rpc/from-lang-pack ::csv-specific-fields-revenue-title)
             csv-specific-fields-serverconversions-title          (rpc/from-lang-pack ::csv-specific-fields-serverconversions-title)
             csv-specific-fields-cvr-title                        (rpc/from-lang-pack ::csv-specific-fields-cvr-title)
             csv-specific-fields-cvrclicks-title                  (rpc/from-lang-pack ::csv-specific-fields-cvrclicks-title)
             csv-specific-fields-cvrimpressions-title             (rpc/from-lang-pack ::csv-specific-fields-cvrimpressions-title)
             csv-specific-fields-cvruniqueimpressions-title       (rpc/from-lang-pack ::csv-specific-fields-cvruniqueimpressions-title)

             csv-specific-fields-purchases-title                  (rpc/from-lang-pack ::csv-specific-fields-purchases-title)
             csv-specific-fields-postclickpurchases-title         (rpc/from-lang-pack ::csv-specific-fields-postclickpurchases-title)
             csv-specific-fields-postviewpurchases-title          (rpc/from-lang-pack ::csv-specific-fields-postviewpurchases-title)
             csv-specific-fields-sameproductskupurchases-title    (rpc/from-lang-pack ::csv-specific-fields-sameproductskupurchases-title)
             csv-specific-fields-sameproductcategorypurchases-title (rpc/from-lang-pack ::csv-specific-fields-sameproductcategorypurchases-title)
             csv-specific-fields-sameproductbrandpurchases-title (rpc/from-lang-pack ::csv-specific-fields-sameproductbrandpurchases-title)
             csv-specific-fields-d1purchases-title               (rpc/from-lang-pack ::csv-specific-fields-d1purchases-title)
             csv-specific-fields-d7purchases-title               (rpc/from-lang-pack ::csv-specific-fields-d7purchases-title)
             csv-specific-fields-d14purchases-title              (rpc/from-lang-pack ::csv-specific-fields-d14purchases-title)
             csv-specific-fields-d30purchases-title              (rpc/from-lang-pack ::csv-specific-fields-d30purchases-title)

             csv-specific-fields-purchasedlineitems-title                  (rpc/from-lang-pack ::csv-specific-fields-purchasedlineitems-title)
             csv-specific-fields-postclickpurchasedlineitems-title         (rpc/from-lang-pack ::csv-specific-fields-postclickpurchasedlineitems-title)
             csv-specific-fields-postviewpurchasedlineitems-title          (rpc/from-lang-pack ::csv-specific-fields-postviewpurchasedlineitems-title)
             csv-specific-fields-sameproductskupurchasedlineitems-title    (rpc/from-lang-pack ::csv-specific-fields-sameproductskupurchasedlineitems-title)
             csv-specific-fields-sameproductcategorypurchasedlineitems-title (rpc/from-lang-pack ::csv-specific-fields-sameproductcategorypurchasedlineitems-title)
             csv-specific-fields-sameproductbrandpurchasedlineitems-title (rpc/from-lang-pack ::csv-specific-fields-sameproductbrandpurchasedlineitems-title)
             csv-specific-fields-d1purchasedlineitems-title               (rpc/from-lang-pack ::csv-specific-fields-d1purchasedlineitems-title)
             csv-specific-fields-d7purchasedlineitems-title               (rpc/from-lang-pack ::csv-specific-fields-d7purchasedlineitems-title)
             csv-specific-fields-d14purchasedlineitems-title              (rpc/from-lang-pack ::csv-specific-fields-d14purchasedlineitems-title)
             csv-specific-fields-d30purchasedlineitems-title              (rpc/from-lang-pack ::csv-specific-fields-d30purchasedlineitems-title)
             csv-specific-fields-purchasedgmv-title                       (rpc/from-lang-pack ::csv-specific-fields-purchasedgmv-title)

             csv-specific-fields-suspiciousclicks-title           (rpc/from-lang-pack ::csv-specific-fields-suspiciousclicks-title)
             csv-specific-fields-testclicks-title                 (rpc/from-lang-pack ::csv-specific-fields-testclicks-title)
             csv-specific-fields-uniqueclicks-title               (rpc/from-lang-pack ::csv-specific-fields-uniqueclicks-title)
             csv-specific-fields-viewconversions-title            (rpc/from-lang-pack ::csv-specific-fields-viewconversions-title)
             csv-specific-fields-flight-startdate-title           (rpc/from-lang-pack ::csv-specific-fields-flight-startdate-title)
             csv-specific-fields-flight-enddate-title             (rpc/from-lang-pack ::csv-specific-fields-flight-enddate-title)
             csv-specific-fields-gmv-title                        (rpc/from-lang-pack ::csv-specific-fields-gmv-title)
             csv-specific-fields-roas-title                       (rpc/from-lang-pack ::csv-specific-fields-roas-title)
             csv-specific-fields-ltvroas-title                    (rpc/from-lang-pack ::csv-specific-fields-ltvroas-title)
             csv-specific-fields-ltvsales-title                   (rpc/from-lang-pack ::csv-specific-fields-ltvsales-title)
             csv-specific-fields-ltvorders-title                  (rpc/from-lang-pack ::csv-specific-fields-ltvorders-title)
             csv-specific-fields-formfactor-title                 (rpc/from-lang-pack ::csv-specific-fields-formfactor-title)
             csv-specific-fields-brandname-title                  (rpc/from-lang-pack ::csv-specific-fields-brandname-title)
             csv-specific-fields-modelname-title                  (rpc/from-lang-pack ::csv-specific-fields-modelname-title)
             csv-specific-fields-advertisedos-title               (rpc/from-lang-pack ::csv-specific-fields-advertisedos-title)
             csv-specific-fields-osmajorversion-title             (rpc/from-lang-pack ::csv-specific-fields-osmajorversion-title)
             csv-specific-fields-osminorversion-title             (rpc/from-lang-pack ::csv-specific-fields-osminorversion-title)
             csv-specific-fields-browser-title                    (rpc/from-lang-pack ::csv-specific-fields-browser-title)
             csv-specific-fields-browsermajorversion-title        (rpc/from-lang-pack ::csv-specific-fields-browsermajorversion-title)
             csv-specific-fields-browserminorversion-title        (rpc/from-lang-pack ::csv-specific-fields-browserminorversion-title)
             csv-specific-fields-bidmodifiertype-title            (rpc/from-lang-pack ::csv-specific-fields-bidmodifiertype-title)
             csv-specific-fields-bidmodifiervalue-title           (rpc/from-lang-pack ::csv-specific-fields-bidmodifiervalue-title)
             csv-specific-fields-bidmodifiermultiplier-title      (rpc/from-lang-pack ::csv-specific-fields-bidmodifiermultiplier-title)
             csv-specific-fields-bidmodifierprice-title           (rpc/from-lang-pack ::csv-specific-fields-bidmodifierprice-title)
             csv-specific-fields-decisionrequestcount-title       (rpc/from-lang-pack ::csv-specific-fields-decisionrequestcount-title)
             csv-specific-fields-requestcount-title               (rpc/from-lang-pack ::csv-specific-fields-requestcount-title)
             csv-specific-fields-rtbcount-title                   (rpc/from-lang-pack ::csv-specific-fields-rtbcount-title)
             csv-specific-fields-rtbmatchedrequestcount-title     (rpc/from-lang-pack ::csv-specific-fields-rtbmatchedrequestcount-title)
             csv-specific-fields-rtbbidrequestcount-title         (rpc/from-lang-pack ::csv-specific-fields-rtbbidrequestcount-title)
             csv-specific-fields-passbackcount-title              (rpc/from-lang-pack ::csv-specific-fields-passbackcount-title)
             csv-specific-fields-validuseragent-title             (rpc/from-lang-pack ::csv-specific-fields-validuseragent-title)
             csv-specific-fields-botfilteringdisabled-title       (rpc/from-lang-pack ::csv-specific-fields-botfilteringdisabled-title)
             csv-specific-fields-invaliduseragent-title           (rpc/from-lang-pack ::csv-specific-fields-invaliduseragent-title)
             ;; DEPRECATED product dimensions
             csv-specific-fields-productskuid-title               (rpc/from-lang-pack ::csv-specific-fields-productskuid-title)
             csv-specific-fields-productskuname-title             (rpc/from-lang-pack ::csv-specific-fields-productskuname-title)
             csv-specific-fields-productmaincategoryname-title    (rpc/from-lang-pack ::csv-specific-fields-productmaincategoryname-title)
             csv-specific-fields-productbrandname-title           (rpc/from-lang-pack ::csv-specific-fields-productbrandname-title)
             csv-specific-fields-productmerchantid-title          (rpc/from-lang-pack ::csv-specific-fields-productmerchantid-title)
             csv-specific-fields-productmerchantname-title        (rpc/from-lang-pack ::csv-specific-fields-productmerchantname-title)
             ;; Disambiguated product dimensions
             ;;
             ;; Promoted product dims
             csv-specific-fields-promotedproductskuid-title               (rpc/from-lang-pack ::csv-specific-fields-promotedproductskuid-title)
             csv-specific-fields-promotedproductskuname-title             (rpc/from-lang-pack ::csv-specific-fields-promotedproductskuname-title)
             csv-specific-fields-promotedproductmaincategoryid-title    (rpc/from-lang-pack ::csv-specific-fields-promotedproductmaincategoryid-title)
             csv-specific-fields-promotedproductmaincategoryname-title    (rpc/from-lang-pack ::csv-specific-fields-promotedproductmaincategoryname-title)
             csv-specific-fields-promotedproductbrandid-title           (rpc/from-lang-pack ::csv-specific-fields-promotedproductbrandid-title)
             csv-specific-fields-promotedproductbrandname-title           (rpc/from-lang-pack ::csv-specific-fields-promotedproductbrandname-title)
             csv-specific-fields-promotedproductmerchantid-title          (rpc/from-lang-pack ::csv-specific-fields-promotedproductmerchantid-title)
             csv-specific-fields-promotedproductmerchantname-title        (rpc/from-lang-pack ::csv-specific-fields-promotedproductmerchantname-title)
             ;; Purchased product dims
             csv-specific-fields-purchasedproductskuid-title               (rpc/from-lang-pack ::csv-specific-fields-purchasedproductskuid-title)
             csv-specific-fields-purchasedproductskuname-title             (rpc/from-lang-pack ::csv-specific-fields-purchasedproductskuname-title)
             csv-specific-fields-purchasedproductmaincategoryid-title    (rpc/from-lang-pack ::csv-specific-fields-purchasedproductmaincategoryid-title)
             csv-specific-fields-purchasedproductmaincategoryname-title    (rpc/from-lang-pack ::csv-specific-fields-purchasedproductmaincategoryname-title)
             csv-specific-fields-purchasedproductbrandid-title           (rpc/from-lang-pack ::csv-specific-fields-purchasedproductbrandid-title)
             csv-specific-fields-purchasedproductbrandname-title           (rpc/from-lang-pack ::csv-specific-fields-purchasedproductbrandname-title)
             csv-specific-fields-purchasedproductmerchantid-title          (rpc/from-lang-pack ::csv-specific-fields-purchasedproductmerchantid-title)
             csv-specific-fields-purchasedproductmerchantname-title        (rpc/from-lang-pack ::csv-specific-fields-purchasedproductmerchantname-title)

             ;; Geo dimensions
             csv-specific-fields-city-title             (rpc/from-lang-pack ::csv-specific-fields-city-title)
             csv-specific-fields-regioncode-title       (rpc/from-lang-pack ::csv-specific-fields-regioncode-title)
             csv-specific-fields-countrycode-title      (rpc/from-lang-pack ::csv-specific-fields-countrycode-title)

             ;; See comment at the top of function regarding bug to do with pairs of pairs
             _ nil
             ]



            {"advertisername"            {:title  csv-specific-fields-advertisername-title
                                          :format (link-formatter "advertiserid")}
             "campaignname"              {:title  csv-specific-fields-campaignname-title
                                          :format (link-formatter "campaignid")}
             "walletid"                  {:title  csv-specific-fields-walletid-title}
             "walletname"                {:title  csv-specific-fields-walletname-title}
             "walletexternalid"          {:title  csv-specific-fields-walletexternalid-title}
             "walletcurrency"            {:title  csv-specific-fields-walletcurrency-title}
             "fillrate"                  {:title  "Fill Rate"
                                          :class  "percent"
                                          :format (format-percent 2)}
             "clickconversions"          {:title  csv-specific-fields-clickconversions-title
                                          :class  "int"
                                          :format format-human-integer}
             "clicks"                    {:title  csv-specific-fields-clicks-title
                                          :class  "int"
                                          :format format-human-integer}
             "ctr"                       {:title  csv-specific-fields-ctr-title
                                          :class  "percent"
                                          :format (format-percent 2)}
             "duplicateimpressionclicks" {:title csv-specific-fields-duplicateimpressionsclicks-title
                                          :class "int"}
             "duplicateipclicks"         {:title csv-specific-fields-duplicateipclicks-title
                                          :class "int"}
             "ecpm"                      {:title  csv-specific-fields-ecpm-title
                                          :class  "dollars"
                                          ;; It's a bit weird that this value is
                                          ;; handed to us, since it's just a
                                          ;; function of revenue and
                                          ;; impressions. But we have no way as
                                          ;; I write this to compute a column
                                          ;; from other columns.
                                          :format format-currency}
             "cpc"                      {:title  csv-specific-fields-cpc-title
                                         :class  "dollars"
                                          ;; It's a bit weird that this value is
                                          ;; handed to us, since it's just a
                                          ;; function of revenue and
                                          ;; impressions. But we have no way as
                                          ;; I write this to compute a column
                                          ;; from other columns.
                                         :format format-currency}
             "end"                       {:title          csv-specific-fields-end-title
                                          :format         format-datetime
                                          :summary-format (fn [& _] "")
                                          :hidden?        true}
             "ordinal"                   {:hidden? true}
             "eventcount"                {:hidden? true}
             "eventid"                   {:hidden? true}
             "eventname"                 {:hidden? true}
             "flatrateevent"             {:hidden? true}
             "flightname"                {:title  csv-specific-fields-flightname-title
                                          :format (link-formatter "flightid")}
             "impressions"               {:title  csv-specific-fields-impressions-title
                                          :class  "int"
                                          :format format-human-integer}
             "unfilteredimpressions"     {:title  csv-specific-fields-unfilteredimpressions-title
                                          :class  "int"
                                          :format format-human-integer}
             "invaliduaimpressions"      {:title  csv-specific-fields-invaliduaimpressions-title
                                          :class  "int"
                                          :format format-human-integer}
             "duplicateimpressions"      {:title  csv-specific-fields-duplicateimpressions-title
                                          :class  "int"
                                          :format format-human-integer}
             "suspiciousimpressions"     {:title  csv-specific-fields-suspiciousimpressions-title
                                          :class  "int"
                                          :format format-human-integer}
             "uniqueimpressions"         {:title  csv-specific-fields-uniqueimpressions-title
                                          :class  "int"
                                          :format format-human-integer}
             "rawimpressions"            {:title  csv-specific-fields-rawimpressions-title
                                          :class  "int"
                                          :format format-human-integer}
             "invaliduaclicks"           {:title csv-specific-fields-invaliduaclicks-title
                                          :class "int"}
             "price"                     {:title  csv-specific-fields-price-title
                                          :class  "dollars"
                                          :format format-currency}
             "ratetype"                  {:title csv-specific-fields-ratetype-title}
             "rawclicks"                 {:title  csv-specific-fields-rawclicks-title
                                          :class  "int"
                                          :format format-human-integer}
             "revenue"                   {:title  csv-specific-fields-revenue-title
                                          :class  "float"
                                          :format format-revenue}
             "serverconversions"         {:title  csv-specific-fields-serverconversions-title
                                          :class  "int"
                                          :format format-human-integer}
             "cvr"                       {:title  csv-specific-fields-cvr-title
                                          :class  "percent"
                                          :format (format-percent 2)}
             "cvrimpressions"            {:title  csv-specific-fields-cvrimpressions-title
                                          :class  "percent"
                                          :format (format-percent 2)}
             "cvruniqueimpressions"      {:title  csv-specific-fields-cvruniqueimpressions-title
                                          :class  "percent"
                                          :format (format-percent 2)}
             "cvrclicks"                 {:title  csv-specific-fields-cvrclicks-title
                                          :class  "percent"
                                          :format (format-percent 2)}

             "purchases"                 {:title  csv-specific-fields-purchases-title
                                          :class  "int"
                                          :format format-human-integer}

             "postclickpurchases"           {:title  csv-specific-fields-postclickpurchases-title
                                             :class  "int"
                                             :format format-human-integer}
             "postviewpurchases"            {:title  csv-specific-fields-postviewpurchases-title
                                             :class  "int"
                                             :format format-human-integer}
             "sameproductskupurchases"      {:title  csv-specific-fields-sameproductskupurchases-title
                                             :class  "int"
                                             :format format-human-integer}
             "sameproductcategorypurchases" {:title  csv-specific-fields-sameproductcategorypurchases-title
                                             :class  "int"
                                             :format format-human-integer}
             "sameproductbrandpurchases"    {:title  csv-specific-fields-sameproductbrandpurchases-title
                                             :class  "int"
                                             :format format-human-integer}
             "d1purchases"                  {:title  csv-specific-fields-d1purchases-title
                                             :class  "int"
                                             :format format-human-integer}
             "d7purchases"                  {:title  csv-specific-fields-d7purchases-title
                                             :class  "int"
                                             :format format-human-integer}
             "d14purchases"                 {:title  csv-specific-fields-d14purchases-title
                                             :class  "int"
                                             :format format-human-integer}
             "d30purchases"                 {:title  csv-specific-fields-d30purchases-title
                                             :class  "int"
                                             :format format-human-integer}

             "purchasedlineitems"                 {:title  csv-specific-fields-purchasedlineitems-title
                                                   :class  "int"
                                                   :format format-human-integer}

             "postclickpurchasedlineitems"           {:title  csv-specific-fields-postclickpurchasedlineitems-title
                                                      :class  "int"
                                                      :format format-human-integer}
             "postviewpurchasedlineitems"            {:title  csv-specific-fields-postviewpurchasedlineitems-title
                                                      :class  "int"
                                                      :format format-human-integer}
             "sameproductskupurchasedlineitems"      {:title  csv-specific-fields-sameproductskupurchasedlineitems-title
                                                      :class  "int"
                                                      :format format-human-integer}
             "sameproductcategorypurchasedlineitems" {:title  csv-specific-fields-sameproductcategorypurchasedlineitems-title
                                                      :class  "int"
                                                      :format format-human-integer}
             "sameproductbrandpurchasedlineitems"    {:title  csv-specific-fields-sameproductbrandpurchasedlineitems-title
                                                      :class  "int"
                                                      :format format-human-integer}
             "d1purchasedlineitems"                  {:title  csv-specific-fields-d1purchasedlineitems-title
                                                      :class  "int"
                                                      :format format-human-integer}
             "d7purchasedlineitems"                  {:title  csv-specific-fields-d7purchasedlineitems-title
                                                      :class  "int"
                                                      :format format-human-integer}
             "d14purchasedlineitems"                 {:title  csv-specific-fields-d14purchasedlineitems-title
                                                      :class  "int"
                                                      :format format-human-integer}
             "d30purchasedlineitems"                 {:title  csv-specific-fields-d30purchasedlineitems-title
                                                      :class  "int"
                                                      :format format-human-integer}

             "purchasedgmv"              {:title  csv-specific-fields-purchasedgmv-title
                                          :class  "float"
                                          :format format-revenue}

             "start"            {:class          "date"
                                 :format         format-datetime
                                 :summary-format (constantly "Total")}
             "suspiciousclicks" {:title  csv-specific-fields-suspiciousclicks-title
                                 :class  "int"
                                 :format format-human-integer}
             "testclicks"       {:title  csv-specific-fields-testclicks-title
                                 :class  "int"
                                 :format format-human-integer}
             "uniqueclicks"     {:title  csv-specific-fields-uniqueclicks-title
                                 :class  "int"
                                 :format format-human-integer}
             "viewconversions"  {:title  csv-specific-fields-viewconversions-title
                                 :class  "int"
                                 :format format-human-integer}
             "flightstartdate"  {:title csv-specific-fields-flight-startdate-title
                                 :class "date"}
             "flightenddate"    {:title csv-specific-fields-flight-enddate-title
                                 :class "date"}
             "gmv"              {:title  csv-specific-fields-gmv-title
                                 :class  "float"
                                 :format format-revenue}
             "roas"             {:title  csv-specific-fields-roas-title
                                 :class  "float"
                                 :format (format-percent 0)}
             "ltvroas"           {:title  csv-specific-fields-ltvroas-title
                                 :class  "float"
                                 :format (format-percent 0)}
             "ltvsales"           {:title  csv-specific-fields-ltvsales-title
                                 :class  "float"
                                 :format format-revenue}
             "ltvorders"          {:title  csv-specific-fields-ltvorders-title
                                 :class  "int"
                                 :format format-human-integer}
             "formfactor"          {:title csv-specific-fields-formfactor-title
                                    :format (flatrate-formatter)}
             "brandname"           {:title csv-specific-fields-brandname-title
                                    :format (flatrate-formatter)}
             "modelname"           {:title csv-specific-fields-modelname-title
                                    :format (flatrate-formatter)}
             "advertisedos"        {:title csv-specific-fields-advertisedos-title
                                    :format (flatrate-formatter)}
             "osmajorversion"      {:title csv-specific-fields-osmajorversion-title
                                    :format (flatrate-formatter)}
             "osminorversion"      {:title csv-specific-fields-osminorversion-title
                                    :format (flatrate-formatter)}
             "browser"             {:title csv-specific-fields-browser-title
                                    :format (flatrate-formatter)}
             "browsermajorversion" {:title csv-specific-fields-browsermajorversion-title
                                    :format (flatrate-formatter)}
             "browserminorversion" {:title csv-specific-fields-browserminorversion-title
                                    :format (flatrate-formatter)}

             "city"             {:title csv-specific-fields-city-title
                                 :format (flatrate-formatter)}
             "regioncode"       {:title csv-specific-fields-regioncode-title
                                 :format (flatrate-formatter)}
             "countrycode"      {:title csv-specific-fields-countrycode-title
                                 :format (flatrate-formatter)}

             "bidmodifiertype"       {:title csv-specific-fields-bidmodifiertype-title
                                      :format (flatrate-formatter)}
             "bidmodifiervalue"      {:title csv-specific-fields-bidmodifiervalue-title
                                      :format (flatrate-formatter)}
             "bidmodifiermultiplier" {:title csv-specific-fields-bidmodifiermultiplier-title
                                      :format (flatrate-formatter)}
             "bidmodifierprice"      {:title csv-specific-fields-bidmodifierprice-title
                                      :format (flatrate-formatter)}

             "decisionrequestcount" {:title  csv-specific-fields-decisionrequestcount-title
                                     :class  "int"
                                     :format format-human-integer}
             "requestcount"         {:title  csv-specific-fields-requestcount-title
                                     :class  "int"
                                     :format format-human-integer}

             "rtbcount"                {:title csv-specific-fields-rtbcount-title}
             "rtbmatchedrequestcount"  {:title csv-specific-fields-rtbmatchedrequestcount-title}
             "rtbbidrequestcount"      {:title csv-specific-fields-rtbbidrequestcount-title}
             "passbackcount"           {:title csv-specific-fields-passbackcount-title}
             "validuseragent"          {:title csv-specific-fields-validuseragent-title}
             "botfilteringdisabled"    {:title csv-specific-fields-botfilteringdisabled-title}
             "invaliduseragent"        {:title csv-specific-fields-invaliduseragent-title}
             "productskuid"            {:title csv-specific-fields-productskuid-title
                                        :format (flatrate-formatter)}
             "productskuname"          {:title csv-specific-fields-productskuname-title
                                        :format (flatrate-formatter)}
             "productmaincategoryname" {:title csv-specific-fields-productmaincategoryname-title
                                        :format (flatrate-formatter)}
             "productbrandname"        {:title csv-specific-fields-productbrandname-title
                                        :format (flatrate-formatter)}
             "productmerchantid"       {:title csv-specific-fields-productmerchantid-title
                                        :format (flatrate-formatter)}
             "productmerchantname"     {:title csv-specific-fields-productmerchantname-title
                                        :format (flatrate-formatter)}
             "promotedproductskuid"            {:title csv-specific-fields-promotedproductskuid-title
                                                :format (flatrate-formatter)}
             "promotedproductskuname"          {:title csv-specific-fields-promotedproductskuname-title
                                                :format (flatrate-formatter)}
             "promotedproductmaincategoryid"   {:title csv-specific-fields-promotedproductmaincategoryid-title
                                                :format (flatrate-formatter)}
             "promotedproductmaincategoryname" {:title csv-specific-fields-promotedproductmaincategoryname-title
                                                :format (flatrate-formatter)}
             "promotedproductbrandid"          {:title csv-specific-fields-promotedproductbrandid-title
                                                :format (flatrate-formatter)}
             "promotedproductbrandname"        {:title csv-specific-fields-promotedproductbrandname-title
                                                :format (flatrate-formatter)}
             "promotedproductmerchantid"       {:title csv-specific-fields-promotedproductmerchantid-title
                                                :format (flatrate-formatter)}
             "promotedproductmerchantname"     {:title csv-specific-fields-promotedproductmerchantname-title
                                                :format (flatrate-formatter)}
             "purchasedproductskuid"            {:title csv-specific-fields-purchasedproductskuid-title
                                                :format (flatrate-formatter)}
             "purchasedproductskuname"          {:title csv-specific-fields-purchasedproductskuname-title
                                                :format (flatrate-formatter)}
             "purchasedproductmaincategoryid"   {:title csv-specific-fields-purchasedproductmaincategoryid-title
                                                :format (flatrate-formatter)}
             "purchasedproductmaincategoryname" {:title csv-specific-fields-purchasedproductmaincategoryname-title
                                                :format (flatrate-formatter)}
             "purchasedproductbrandid"          {:title csv-specific-fields-purchasedproductbrandid-title
                                                :format (flatrate-formatter)}
             "purchasedproductbrandname"        {:title csv-specific-fields-purchasedproductbrandname-title
                                                :format (flatrate-formatter)}
             "purchasedproductmerchantid"       {:title csv-specific-fields-purchasedproductmerchantid-title
                                                :format (flatrate-formatter)}
             "purchasedproductmerchantname"     {:title csv-specific-fields-purchasedproductmerchantname-title
                                                :format (flatrate-formatter)}}))


(defc= request-schema-lowered
  "The request schema, but with lower case keys. This is because API
  proxy requires exact casing on the report requests, but the report
  that comes back has columns with lower-cased names."
  (reduce-kv (fn [m k v] (assoc m (str/lower-case k) v))
             {}
             request-schema))

;; These fields don't depend on anything that needs to be fetched from
;; the server. The report-schema below relies on the language pack
;; which is fetched asynchronously. It used to be that we had these
;; fields combined with report-schema, but as a function call. So for
;; every single cell, we would run all this code to build the
;; mapping. This caused the UI to lock for 20 minutes when there were
;; large reports.
(defc= report-static-fields
  (->
   (for [[id name] comm/report-name-fields]
     (let [title (get-in request-schema-lowered [id :title])]
       {id   {:title   title
              :hidden? true}
        name {:title title}}))
   (->> (reduce merge))))

(def report-schema
  "This is basically the schema for report data. Fields are (lowercase) keys
  used in the reporting API. Values are maps with the following keys:

  :title - the human-readable version of the key
  :class - sets the CSS class for the <td> that contains the object returned by the :format function.
  :format - a function from the programmatic value to UI (a DOM element)
  :summary-format - a format function only to use in the 'summary row' at the bottom of the report."
  (formula-of [report-static-fields csv-specific-fields]
              (merge report-static-fields csv-specific-fields)))

(def report-time-period
  (formula-of [report-meta]
    (comm/report-time-period report-meta)))

(def report-field-formats-fn
  ;; At one point, the formats of the various fields depended on
  ;; things like the report time period. It doesn't any more, but it
  ;; seems useful to keep this abstraction in place, since adding it
  ;; back in if we need it would be a pain.
  (cell= (fn [] report-schema)))

(defelem filter-picker-row
  "Returns a row representing the chosen filter state, which should be a cell of
  a vector like [name id]"
  [{:keys [state remove-fn report-filters]} _]
  (cell-let [[name val] state]
    (tr
      (td (cell= (get report-filters name)))
      (td (cell=
           (if (or (= (:__title__ val) "keyword")
                   (contains? product-text-filters (:__title__ val)))
              (:id val)
              (:__title__ val))))
      (td (a {:alt   "foo"
              :click #(remove-fn @state)}
             (icon/remove))))))

(defelem filter-picker-typeahead
  [{:keys [param-name= state multi-selected] :as attr} _]
  (div
   (span :toggle (cell= (not (contains?
                               (set/union
                                 (medley/filter-vals :filter-rpc request-schema)
                                 (medley/filter-vals :filter-values request-schema))
                                param-name=)))
         (text (cell= (str ~(rpc/from-lang-pack ::filter-picker-typeahead)
                           " "
                           param-name=))))
   (formula-of [request-schema]
               (for [[name {:keys [results results-fn filter-rpc filter-values display-fn
                                   selected-display-fn]
                            :or   {display-fn          :__title__
                                   selected-display-fn :__title__}
                            :as   _schema}]
                     request-schema
                     :when (or filter-rpc filter-values)]
                 (div :toggle (cell= (= name param-name=))
                      :css {:margin "1em 0"}
                      (if filter-rpc
                        (form/typeahead
                          :state               state
                          :display-fn          display-fn
                          :selected-display-fn selected-display-fn
                          :value-fn            #(select-keys % [:id :__title__])
                          :results             (if results-fn
                                                 (results-fn attr)
                                                 results)
                          :rpc                 filter-rpc
                          :keep-open?          true
                          :multi-selected      multi-selected)
                        (form/local-typeahead
                          :state               state
                          :display-fn          display-fn
                          :selected-display-fn selected-display-fn
                          :value-fn            #(select-keys % [:id :__title__])
                          :page-size           10
                          :options             filter-values
                          :keep-open?          true
                          :multi-selected      multi-selected)))))))

(defn add-filters! [ranks next-rank! state remove!]
  (fn [filters]
    (doseq [fltr filters]
      (if (some #(= % fltr) @state)
        ;; If already selected, remove it
        (remove! fltr)
        ;; If not selected, add it
        (do
          (swap! ranks assoc fltr (next-rank!))
          (swap! state conj fltr))))))

(defelem filter-picker
  [{:keys [state exclusion-filters?]} _]
  (let [report-filters  (if exclusion-filters? report-exclusion-filters report-filters)
        new-filter-type (cell (ffirst @report-filters))
        new-filter-val  (cell nil)
        new-filter      (cell= [new-filter-type new-filter-val])
        next-rank!      (let [r (atom 0)] #(swap! r inc))
        ranks           (cell {})
        sorted-filters  (cell= (sort-by #(get ranks % 0) state)) 
        text-input?     (set/union #{"keyword" "city"} device-group-bys bid-modifier-text-filters placement-category-text-filters search-term-text-filters product-group-bys deprecated-product-group-bys product-text-filters)
        numeric-input?  (set/union bid-modifier-numeric-filters)
        freeform-input? (set/union text-input? numeric-input?)
        remove!         (fn [filter]
                          (dosync
                           (swap! ranks dissoc filter)
                           (swap! state disj filter)))
        add-filters!    (add-filters! ranks next-rank! state remove!) 
        add!            #(let [new-filter-type' @new-filter-type]
                           (add-filters!
                            (if (freeform-input? new-filter-type')
                              (for [k     (str/split (:id @new-filter-val)
                                                     #",+\s*")
                                    :when (not= 0 (count (str/trim k)))]
                                [new-filter-type' {:id k :__title__ k}])
                              [@new-filter]))
                           (reset! new-filter-val nil))
       ]
    ;; Automatically add filter selections if it's a typehead, upon making a
    ;; selection in the typeahead.
    (util/on-change
      (cell= [new-filter-type new-filter-val])
      (fn [[new-filter-type' new-filter-val']]
        (when (and new-filter-val' (not (freeform-input? new-filter-type')))
          (with-timeout 0
            (add!)))))
    
    ;; Clear the new-filter-val if the filter type changed.
    ;; This prevents the UI from selecting an outdated/invalid value with the new selected filter type.
    (util/on-change new-filter-type 
                    #(reset! new-filter-val nil))
    
    (div
      (grid/grid
        :css {:margin 0 :padding "6px"}
        (grid/row
          (grid/item
            (div
              :class "select"
              (input/select
                :id "date-group"
                :state new-filter-type
                :class "form-control"
                (for-tpl [[value label] report-filters]
                  (option :value value label))))))
        (grid/row
          (grid/item
            (cond-tpl
              (cell= (text-input? new-filter-type))
                (input/input
                  :class "form-control"
                  :type  "text"
                  :css   {:margin "1em 0"}
                  :state (cell= (:__title__ new-filter-val)
                                #(reset! new-filter-val {:id % :__title__ %})))
              (cell= (numeric-input? new-filter-type))
                (input/input
                  :class "form-control"
                  :type  "number"
                  :css   {:margin "1em 0"}
                  :state (cell= (:__title__ new-filter-val)
                                #(reset! new-filter-val {:id % :__title__ %})))
              :else
                (filter-picker-typeahead
                  :param-name=        new-filter-type
                  :state              new-filter-val
                  :exclusion-filters? exclusion-filters?
                  :multi-selected     state))
            ;; If it's a freeform input (instead of a typeahead), there is an
            ;; explicit "Add" button that the user clicks after they're done
            ;; typing into the text input.
        (when-tpl (cell= (freeform-input? new-filter-type))
                  (grid/item
                   (button/inline
                    :disabled (cell= (contains? state new-filter))
                    :click add!
                    [(span :class "glyphicon glyphicon-plus-sign")
                     (span
                      (rpc/from-lang-pack ::filter-picker-button-add-text))]))))))
      ;; (util/on-change new-filter-type
      ;;                 (fn [_] (reset! new-filter-val nil)))
     (component/table
      (thead
       (tr
        (th (rpc/from-lang-pack ::filter-picker-table-column-header-filter))
        (th (rpc/from-lang-pack ::filter-picker-table-column-header-value))
        (th "")))
      (tbody
       (for-tpl [f sorted-filters]
                (filter-picker-row
                 {:remove-fn      remove!
                  :state          f
                  :report-filters report-filters})))))))

(helix/defnc InfoTooltip [{:keys [title]}]
  (helix/$ Tooltip {:title title}
           (helix/$ Help {:fontSize "small"
                          :style    #js {:color         "#888"
                                         :fontSize      "16px"
                                         :cursor        "help"
                                         :verticalAlign "middle"}})))

(defelem group-by-checkbox
  [attr _]
  (let [{:keys    [group-bys value label toggle css parent? parent-toggled? info-tooltip]} attr]
    (div :class   "checkbox"
         :toggle  (or toggle (cell= true))
         :css     (merge {:margin "0px"} css)
         (hoplon.core/label
          {:css {:display "flex"
                 :justify-content "space-between"
                 :align-items "center"
                 }}
          (div
           :css {:display     "flex"
                 :align-items "center"
                 :gap         "6px"}
           (span :class "switch"
                 (input/checkset :state group-bys :value value)
                 (i))
           label
           (when info-tooltip
             (helix-hoplon/mount-react-js InfoTooltip {:title info-tooltip})))
          (when parent?
            (if-tpl parent-toggled?
                    (icon/up-small
                     {:css {:color "lightgray"}})
                    (icon/down-small
                     {:css {:color "lightgray"}})))))))

(defelem group-by-hierarchy-parent-checkbox
  [attr _]
  (let [{:keys [group-bys value label toggle css]} attr]
   (group-by-checkbox {:css css
                       :toggle (or toggle (cell= true))
                       :group-bys group-bys
                       :value value
                       :label label
                       :parent? true
                       :parent-toggled? (cell= (contains? group-bys value))})))

(defelem group-by-hierarchy-child-checkbox
  [attr _]
  (let [{:keys [group-bys value label toggle css info-tooltip]} attr]
   (group-by-checkbox {:css (merge {:scale 0.9
                                    :margin 0
                                    :width "100%"
                                    :margin-left "10px"} css)
                       :toggle (or toggle (cell= true))
                       :group-bys group-bys
                       :value value
                       :label label
                       :info-tooltip info-tooltip})))

(defelem group-by-flight-hierarchy-checkbox
  [attr _]
  (let [price-info (formula-of [request-schema] (get request-schema "price"))
        {:keys [group-bys value label]} attr]
    (div
     (group-by-hierarchy-parent-checkbox {:group-bys group-bys
                                          :value value
                                          :label label})
     (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys value))
                                         :group-bys group-bys
                                         :value "price"
                                         :label (:title @price-info)}))))

(defelem group-by-device-hierarchy-checkbox
  [attr _]
  (let [device-info (formula-of [request-schema] (get request-schema "device"))
        form-factor-info (formula-of [request-schema] (get request-schema "formFactor"))
        brand-info (formula-of [request-schema] (get request-schema "brandName"))
        model-info (formula-of [request-schema] (get request-schema "modelName"))
        advertised-os-info (formula-of [request-schema] (get request-schema "advertisedOs"))
        os-major-version-info (formula-of [request-schema] (get request-schema "osMajorVersion"))
        os-minor-version-info (formula-of [request-schema] (get request-schema "osMinorVersion"))
        browser-info (formula-of [request-schema] (get request-schema "browser"))
        browser-major-version-info (formula-of [request-schema] (get request-schema "browserMajorVersion"))
        browser-minor-version-info (formula-of [request-schema] (get request-schema "browserMinorVersion"))

        {:keys [group-bys value label]} attr]

    (div
      (group-by-hierarchy-parent-checkbox {:group-bys group-bys
                                           :value     (cell= "device")
                                           :label     (:title @device-info)})

      (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "device"))
                                          :group-bys group-bys
                                          :value (cell= "formFactor")
                                          :label (:title @form-factor-info)})

      (group-by-hierarchy-child-checkbox {:toggle    (cell= (contains? group-bys "device"))
                                          :group-bys group-bys
                                          :value     (cell= "brandName")
                                          :label     (:title @brand-info)})

      (group-by-hierarchy-child-checkbox {:toggle    (cell= (contains? group-bys "device"))
                                          :group-bys group-bys
                                          :value     (cell= "modelName")
                                          :label     (:title @model-info)})

      (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "device"))
                                          :group-bys group-bys
                                          :value (cell= "advertisedOs")
                                          :label (:title @advertised-os-info)})

      (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "device"))
                                          :group-bys group-bys
                                          :value (cell= "osMajorVersion")
                                          :label (:title @os-major-version-info)})

      (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "device"))
                                          :group-bys group-bys
                                          :value (cell= "osMinorVersion")
                                          :label (:title @os-minor-version-info)})

      (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "device"))
                                          :group-bys group-bys
                                          :value (cell= "browser")
                                          :label (:title @browser-info)})

      (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "device"))
                                          :group-bys group-bys
                                          :value (cell= "browserMajorVersion")
                                          :label (:title @browser-major-version-info)})

      (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "device"))
                                          :group-bys group-bys
                                          :value (cell= "browserMinorVersion")
                                          :label (:title @browser-minor-version-info)}))))


(defelem group-by-bid-modifier-hierarchy-checkbox
  [attr _]
  (let [bid-modifier-info (formula-of [request-schema] (get request-schema "bidModifier"))
        bid-modifier-type-info (formula-of [request-schema] (get request-schema "bidModifierType"))
        bid-modifier-value-info (formula-of [request-schema] (get request-schema "bidModifierValue"))
        bid-modifier-multiplier-info (formula-of [request-schema] (get request-schema "bidModifierMultiplier"))
        bid-modifier-price-info (formula-of [request-schema] (get request-schema "bidModifierPrice"))

        {:keys [group-bys value label]} attr]

    (div
      (group-by-hierarchy-parent-checkbox {:group-bys group-bys
                                           :value     (cell= "bidModifier")
                                           :label     (:title @bid-modifier-info)})

      (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "bidModifier"))
                                          :group-bys group-bys
                                          :value (cell= "bidModifierType")
                                          :label (:title @bid-modifier-type-info)})

      (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "bidModifier"))
                                          :group-bys group-bys
                                          :value (cell= "bidModifierValue")
                                          :label (:title @bid-modifier-value-info)})

      (group-by-hierarchy-child-checkbox {:toggle    (cell= (contains? group-bys "bidModifier"))
                                          :group-bys group-bys
                                          :value     (cell= "bidModifierMultiplier")
                                          :label     (:title @bid-modifier-multiplier-info)})

      (group-by-hierarchy-child-checkbox {:toggle    (cell= (contains? group-bys "bidModifier"))
                                          :group-bys group-bys
                                          :value     (cell= "bidModifierPrice")
                                          :label     (:title @bid-modifier-price-info)}))))

(defelem group-by-product-hierarchy-checkbox
  [attr _]
  (let [product-info (formula-of [request-schema] (get request-schema "product"))
        promoted-product-sku-id-info (formula-of [request-schema] (get request-schema "promotedProductSkuId"))
        promoted-product-main-category-id-info (formula-of [request-schema] (get request-schema "promotedProductMainCategoryId"))
        promoted-product-brand-id-info (formula-of [request-schema] (get request-schema "promotedProductBrandId"))
        promoted-product-merchant-id-info (formula-of [request-schema] (get request-schema "promotedProductMerchantId"))

        purchased-product-sku-id-info (formula-of [request-schema] (get request-schema "purchasedProductSkuId"))
        purchased-product-main-category-id-info (formula-of [request-schema] (get request-schema "purchasedProductMainCategoryId"))
        purchased-product-brand-id-info (formula-of [request-schema] (get request-schema "purchasedProductBrandId"))
        purchased-product-merchant-id-info (formula-of [request-schema] (get request-schema "purchasedProductMerchantId"))

        {:keys [group-bys value label]} attr]

    (div
     (group-by-hierarchy-parent-checkbox {:group-bys group-bys
                                          :value     (cell= "product")
                                          :label     (:title @product-info)})

     ;; Promoted product dimensions
     (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "product"))
                                         :group-bys group-bys
                                         :value (cell= "promotedProductSkuId")
                                         :label (:title @promoted-product-sku-id-info)
                                         :info-tooltip (rpc/from-lang-pack ::request-schema-field-promotedproductskuid-tooltip)})

     (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "product"))
                                         :group-bys group-bys
                                         :value (cell= "promotedProductMainCategoryId")
                                         :label (:title @promoted-product-main-category-id-info)})

     (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "product"))
                                         :group-bys group-bys
                                         :value (cell= "promotedProductBrandId")
                                         :label (:title @promoted-product-brand-id-info)})

     (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "product"))
                                         :group-bys group-bys
                                         :value (cell= "promotedProductMerchantId")
                                         :label (:title @promoted-product-merchant-id-info)})

     ;; Purchased product dimensions
     (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "product"))
                                         :group-bys group-bys
                                         :value (cell= "purchasedProductSkuId")
                                         :label (:title @purchased-product-sku-id-info)})

     (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "product"))
                                         :group-bys group-bys
                                         :value (cell= "purchasedProductMainCategoryId")
                                         :label (:title @purchased-product-main-category-id-info)})

     (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "product"))
                                         :group-bys group-bys
                                         :value (cell= "purchasedProductBrandId")
                                         :label (:title @purchased-product-brand-id-info)})

     (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "product"))
                                         :group-bys group-bys
                                         :value (cell= "purchasedProductMerchantId")
                                         :label (:title @purchased-product-merchant-id-info)}))))

(defelem group-by-deprecated-product-hierarchy-checkbox
  [attr _]
  (let [product-info (formula-of [request-schema] (get request-schema "deprecatedProduct"))
        product-sku-id-info (formula-of [request-schema] (get request-schema "productSkuId"))
        product-main-category-id-info (formula-of [request-schema] (get request-schema "productMainCategoryId"))
        product-brand-id-info (formula-of [request-schema] (get request-schema "productBrandId"))
        product-merchant-id-info (formula-of [request-schema] (get request-schema "productMerchantId"))
        {:keys [group-bys value label]} attr]

    (div
     (group-by-hierarchy-parent-checkbox {:group-bys group-bys
                                          :value     (cell= "deprecatedProduct")
                                          :label     (:title @product-info)})

     (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "deprecatedProduct"))
                                         :group-bys group-bys
                                         :value (cell= "productSkuId")
                                         :label (:title @product-sku-id-info)})

     (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "deprecatedProduct"))
                                         :group-bys group-bys
                                         :value (cell= "productMainCategoryId")
                                         :label (:title @product-main-category-id-info)})

     (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "deprecatedProduct"))
                                         :group-bys group-bys
                                         :value (cell= "productBrandId")
                                         :label (:title @product-brand-id-info)})

     (group-by-hierarchy-child-checkbox {:toggle (cell= (contains? group-bys "deprecatedProduct"))
                                         :group-bys group-bys
                                         :value (cell= "productMerchantId")
                                         :label (:title @product-merchant-id-info)}))))

(defelem group-bys-picker-inventory
  [attr _]
  (let [{:keys [group-bys]} attr]
    (formula-of [inventory-report-group-bys]
      (div
      (for-tpl [[value label] inventory-report-group-bys]
               (group-by-checkbox
                {:group-bys group-bys
                 :value value
                 :label label
                 :info-tooltip (when (= @value "placementCategory")
                                 (rpc/from-lang-pack ::request-schema-field-placementcategory-tooltip))}))))))

(defelem group-bys-picker-performance
  [attr _]
  (let [{:keys [group-bys]} attr]
    (formula-of [performance-report-group-bys]
                (div
                 (for-tpl [[value label] performance-report-group-bys]
                          (cond
                            (= @value "flightId") (group-by-flight-hierarchy-checkbox {:group-bys group-bys :value value :label label})
                            (= @value "device") (group-by-device-hierarchy-checkbox {:group-bys group-bys})
                            (= @value "bidModifier") (group-by-bid-modifier-hierarchy-checkbox {:group-bys group-bys})
                            (= @value "product") (group-by-product-hierarchy-checkbox {:group-bys group-bys})
                            (= @value "deprecatedProduct") (group-by-deprecated-product-hierarchy-checkbox {:group-bys group-bys})
                            :else (group-by-checkbox {:group-bys group-bys :value value :label label})))))))

(defn datetime-state-from-date
  "Used to update datetimepicker state cell from datepicker cell state
    without triggering accidental timezone conversion since
   datepicker needs to use local date while datetimepicker uses UTC date in its state."
  [cell-state]
  (let [date-str (format-date cell-state "YYYY-MM-DDT00:00:00" {:read-as-utc? false})] ;; making sure format-date drops timepart of date
    (util/as-utc date-str)))

(defn date-state-from-datetime
  "Used to update datepicker state cell using datetimepicker cell state
   explicitly triggering UTC to local conversion since
   datepicker uses local date while datetimepicker requires UTC"
  [cell-state]
  (let [date-str (format-date cell-state "YYYY-MM-DDT00:00:00")]
    (util/start-of-day date-str)))

(defn hourly-reporting? [date-group]
  (= date-group "hour"))

(defelem date-fields-picker
  [attr _]
  (let [{:keys [start-date start-datetime
                end-date   end-datetime
                date-group date-range confirm
                timezone]} attr]

    (add-watch date-group :date-group-changed
               (fn [_ _ old-date-group new-date-group]
                 (cond
                   ;; if the Date Group changed TO Hourly reporting
                   ;; update datetime cells using date cells state
                  (hourly-reporting? new-date-group)   (dosync
                                                        (reset! start-datetime  @start-date)
                                                        (reset! end-datetime    (-> (js/moment. @end-date)
                                                                                    (.add 1 "days")
                                                                                    .toDate)))
                   ;; if the Date Group switched FROM hourly reporting
                   ;; update date cells using datetime cells state
                   (hourly-reporting? old-date-group)   (dosync
                                                         (reset! start-date      (date-state-from-datetime @start-datetime))
                                                         (reset! end-date        (if (or (= (str const/REPORT-TODATE-TODAY) (str @date-range))
                                                                                         (= (str const/REPORT-TODATE-YESTERDAY) (str @date-range)))
                                                                                   (date-state-from-datetime (-> (js/moment. @end-datetime)
                                                                                                                 (.subtract 1 "days")
                                                                                                                 .toDate))
                                                                                   (date-state-from-datetime @end-datetime)))))))

    (form :class "collapsible-mobile-body"
         (div
           (hoplon.core/label
             :for "date-range-type"
             (rpc/from-lang-pack ::date-fields-picker-type-label))
           (div :class "select"
                (input/select
                 :id "date-range-type"
                 :change #(dosync
                           (reset! end-date (util/yesterday))
                           (cond
                             (= @% (str const/REPORT-TODATE-MONTH))    (reset! start-date (util/start-of-month))
                             (= @% (str const/REPORT-TODATE-QUARTER))  (reset! start-date (util/start-of-quarter))
                             (= @% (str const/REPORT-TODATE-YEAR))     (reset! start-date (util/start-of-year))
                             (= @% (str const/REPORT-TODATE-TODAY))
                              (do
                               (reset! start-date (util/start-of-day))
                               (reset! end-date (util/start-of-day)))
                             (= @% (str const/REPORT-TODATE-YESTERDAY))
                             (do
                               (reset! start-date (util/yesterday))
                               (reset! end-date (util/yesterday)))
                             (= @% (str const/REPORT-TODATE-LAST-SEVEN-DAYS))   (reset! start-date (util/days-ago 7))
                             (= @% (str const/REPORT-TODATE-LAST-FOURTEEN-DAYS))   (reset! start-date (util/days-ago 14))
                             (= @% (str const/REPORT-TODATE-LAST-THIRTY-DAYS))   (reset! start-date (util/days-ago 30))
                             (= @% (str const/REPORT-TODATE-LAST-WEEK-SUN-SAT))
                             (let [{:keys [start end]} (util/last-week :sunday)]
                               (reset! start-date start)
                               (reset! end-date end))
                             (= @% (str const/REPORT-TODATE-LAST-WEEK-MON-SUN))
                             (let [{:keys [start end]} (util/last-week :monday)]
                               (reset! start-date start)
                               (reset! end-date end))
                             (= @% (str const/REPORT-TODATE-LAST-MONTH)) 
                             (do
                               (reset! start-date (util/start-of-month 1))
                              (reset! end-date (util/end-of-month 1))))
                          (when (hourly-reporting? @date-group)
                            (reset! start-datetime @start-date)
                            (reset! end-datetime (-> (js/moment. @end-date)
                                                     (.add 1 "days")
                                                     .toDate))))
                 :state date-range
                 :class "form-control"
                 (option :selected (cell= (= date-range const/REPORT-TODATE-CUSTOM))
                         :value const/REPORT-TODATE-CUSTOM
                         (rpc/from-lang-pack (get const/todate-type const/REPORT-TODATE-CUSTOM)))
                 (option :selected (cell= (= date-range const/REPORT-TODATE-MONTH))
                         :value const/REPORT-TODATE-MONTH
                         (rpc/from-lang-pack (get const/todate-type const/REPORT-TODATE-MONTH)))
                 (option :selected (cell= (= date-range const/REPORT-TODATE-QUARTER))
                         :value const/REPORT-TODATE-QUARTER
                         (rpc/from-lang-pack (get const/todate-type const/REPORT-TODATE-QUARTER)))
                 (option :selected (cell= (= date-range const/REPORT-TODATE-YEAR))
                         :value const/REPORT-TODATE-YEAR
                         (rpc/from-lang-pack (get const/todate-type const/REPORT-TODATE-YEAR)))
                 (option :selected (cell= (= date-range const/REPORT-TODATE-TODAY))
                         :value const/REPORT-TODATE-TODAY
                         (rpc/from-lang-pack (get const/todate-type const/REPORT-TODATE-TODAY)))
                 (option :selected (cell= (= date-range const/REPORT-TODATE-YESTERDAY))
                         :value const/REPORT-TODATE-YESTERDAY
                         (rpc/from-lang-pack (get const/todate-type const/REPORT-TODATE-YESTERDAY)))
                 (option :selected (cell= (= date-range const/REPORT-TODATE-LAST-SEVEN-DAYS))
                         :value const/REPORT-TODATE-LAST-SEVEN-DAYS
                         (rpc/from-lang-pack (get const/todate-type const/REPORT-TODATE-LAST-SEVEN-DAYS)))
                 (option :selected (cell= (= date-range const/REPORT-TODATE-LAST-FOURTEEN-DAYS))
                         :value const/REPORT-TODATE-LAST-FOURTEEN-DAYS
                         (rpc/from-lang-pack (get const/todate-type const/REPORT-TODATE-LAST-FOURTEEN-DAYS)))
                 (option :selected (cell= (= date-range const/REPORT-TODATE-LAST-THIRTY-DAYS))
                         :value const/REPORT-TODATE-LAST-THIRTY-DAYS
                         (rpc/from-lang-pack (get const/todate-type const/REPORT-TODATE-LAST-THIRTY-DAYS)))
                 (option :selected (cell= (= date-range const/REPORT-TODATE-LAST-WEEK-SUN-SAT))
                         :value const/REPORT-TODATE-LAST-WEEK-SUN-SAT
                         (rpc/from-lang-pack (get const/todate-type const/REPORT-TODATE-LAST-WEEK-SUN-SAT)))
                 (option :selected (cell= (= date-range const/REPORT-TODATE-LAST-WEEK-MON-SUN))
                         :value const/REPORT-TODATE-LAST-WEEK-MON-SUN
                         (rpc/from-lang-pack (get const/todate-type const/REPORT-TODATE-LAST-WEEK-MON-SUN)))
                 (option :selected (cell= (= date-range const/REPORT-TODATE-LAST-MONTH))
                         :value const/REPORT-TODATE-LAST-MONTH
                         (rpc/from-lang-pack (get const/todate-type const/REPORT-TODATE-LAST-MONTH))))))
         
         (div
          :class "timezone"
          (hoplon.core/label
           :for "timezone"
           (rpc/from-lang-pack ::timezone-label))

          (form/local-typeahead
           :state               timezone
           :display-fn          :name
           :selected-display-fn :name
           :initial-display     (cell=
                                  (when timezone
                                    (timezones/friendly-name-with-offset
                                      (timezones/by-id timezone))))
           :value-fn            :id
           :page-size           10
           :options             (timezones/timezones-with-offsets)))

         (div
          (hoplon.core/label
           :for "start-date"
           (rpc/from-lang-pack ::date-fields-picker-startdate-label))
          (if-tpl (cell= (= date-group "hour"))
            (form/datetime-input
             :id "start-date"
             :disabled (cell= (not= const/REPORT-TODATE-CUSTOM (int date-range)))
             :state start-datetime
             :opts {:datepicker-format "mm/dd/yyyy hh:ii"
                    :date-format "MM/DD/YYYY HH:mm"
                    :minuteStep 60})
            (form/date-input
             :id "start-date"
             :disabled (cell= (not= const/REPORT-TODATE-CUSTOM (int date-range)))
             :state start-date))

          (div :class "checkbox"
               :css {:width "300px" :margin "10px"}
               :toggle (cell= (.isAfter (js/moment. start-date) (js/moment.)))
            (hoplon.core/label {}
              (span :class "switch" (input/checkbox :state confirm :value true) (i))
              (rpc/from-lang-pack ::date-fields-picker-confirm-future-label))))

         (div
          (hoplon.core/label
           :for "end-date"
           (rpc/from-lang-pack ::date-fields-picker-enddate-label))
          (if-tpl (cell= (= date-group "hour"))
            (form/datetime-input
             :id "end-date"
             :disabled (cell= (not= const/REPORT-TODATE-CUSTOM (int date-range)))
             :state end-datetime
             :opts {:datepicker-format "mm/dd/yyyy hh:ii"
                    :date-format "MM/DD/YYYY HH:mm"
                    :minuteStep 60})
            (form/date-input
             :id "end-date"
             :disabled (cell= (not= const/REPORT-TODATE-CUSTOM (int date-range)))
             :state end-date)))


         (div
          (hoplon.core/label
           :for "date-group"
           (rpc/from-lang-pack ::date-fields-picker-date-group-label))
          (div :class "select"
               (input/select
                :id "date-group"
                :state date-group
                :class "form-control"
                (rpc/when-feature :halfHourReporting
                  (option :selected (cell= (= date-group "hour")) :value "hour" (rpc/from-lang-pack ::date-fields-picker-date-group-option-hour)))
                (option :selected (cell= (= date-group "day")) :value "day" (rpc/from-lang-pack ::date-fields-picker-date-group-option-day))
                (option :selected (cell= (= date-group "week")) :value "week"  (rpc/from-lang-pack ::date-fields-picker-date-group-option-week))
                (option :selected (cell= (= date-group "month")) :value "month" (rpc/from-lang-pack ::date-fields-picker-date-group-option-month))
                (option :selected (cell= (= date-group "total")) :value "total" (rpc/from-lang-pack ::date-fields-picker-date-group-option-total))))))))

(defn attr-merge
  "Merges `a` and `b` in a way appropriate to Hoplon attribute maps.
  So for instance, css maps will be merged, but things like click
  handlers will simply be overridden."
  [a b]
  (if (and (map? a) (map? b))
    (merge a b)
    b))

(defelem panel
  [attrs content]
  (with-attr-bindings attrs [title content-css panel-css]
    (div
      (merge-with
        attr-merge
        {:class "report-submit-page-panel" :css panel-css}
        attrs)
      (when title
        (h5 :css {:white-space "nowrap"} title))
      (div
        :class "panel-content"
        :css   content-css
        content))))

(defn prefill-state-from-url
      "base64 encoded JSON with report-meta

       e.g. { Request {Start 2022-08-01T00:00:00, End 2022-08-31T00:00:00,
             TimeZone UTC, TimePeriod day, Parameters [{flightId 123456}],
             GroupBy [creativeId] }}

       Caveat, the code below expects that we are always prefilling for a DAY report, so we dont care about time"
      [byte64prefill]
      (let [prefill-json (.parse js/JSON (crypto/base64-string->string byte64prefill))
            prefill-state (js->clj prefill-json :keywordize-keys false)

            ;; Force the date to the TimeZone provided (most likely GMT/UTC) and remove the timezone (the Z-ulu component)
            ;; so further functions/components don't apply user timezone resulting in date shift
            fix-start-date-fn (fn [date] (->

                                           js/moment
                                           ;; we don't want to do (js/moment. date) as that will apply user timezone, shifting the dates
                                           (.tz date
                                                (get-in prefill-state ["Request" "TimeZone"]))
                                           (.format "YYYY-MM-DD")))
            fix-end-date-fn (fn [date] (-> js/moment
                                           ;; we don't want to do (js/moment. date) as that will apply user timezone, shifting the dates
                                           (.tz (or date (util/utc-now))
                                                (get-in prefill-state ["Request" "TimeZone"]))

                                           ;; add back 1 day, compensate for weird logic "(if schedule 'add 0 days' 'add 1 day')"
                                           (.add 1 "days")
                                           (.format "YYYY-MM-DD")))]
           (->> prefill-state
                (#(update-in %1 ["Request" "Start"] fix-start-date-fn))
                (#(update-in %1 ["Request" "End"] fix-end-date-fn))
                (reset! report-meta))))

(def on-enter
  (r/on-enter*
   {:region "reports"
    :zone "report"}
   (fn [from to]
     (if to
       (with-time (str "here state change. :from " (pr-str from) " to " (pr-str to))
         (dosync
          (reset! report-status ::unknown)
          (reset! report-data nil)
          (reset! error-message nil)
          (reset! error nil)
          (cond
            (= (:entity to) "new")
            (dosync
              (reset! scheduling? false)
              (reset! scheduled-report-editing? false)
              (when-not @copying?
                (reset! report-meta nil)
                (reset-form @form-cells))
              (reset! state :new-report-id)
              (set! js/window.location.hash (str  "#!/"
                                                  @rpc/networkid
                                                  "/reports/"
                                                  (random-uuid)
                                                  (when (:prefill to) (str "/" (:prefill to))))))

            (= (:entity to) "scheduled")
            (do
              (reset! scheduling? true)
              (when-not @copying?
                (reset! report-meta nil)
                (reset-form @form-cells))
              (reset! state :new-report-id)
              (set! js/window.location.hash (str  "#!/"
                                                  @rpc/networkid
                                                  "/reports/"
                                                  (random-uuid))))

            :else
            (let [id (:entity to)]
                 (when (:prefill to) (prefill-state-from-url (:prefill to)))
                 (swap! state
                     (fn [prev-state]
                         (if (= prev-state :new-report-id)
                           prev-state
                           :start)))
              (event! :page-entered id)
              (reset! copying? false)))))
       (dosync
         (reset! scheduled-report-editing? false)
         (reset! scheduling? false)
         (reset! report-scheduled nil)
         (reset! report-meta nil)
         (reset! error-message nil)
         (reset-form @form-cells))))))

(defn process-group-bys-for-backend
  "Technically by our api, flightid means flight and price. At the api-proxy
   level we change this to be expanded to price + flightid. We do this because
   we need to support flightid without price while not breaking what flightid
   currently means. So we introduced flightWithoutPrice.

   When sending to the backend, we need to convert these to what the backend
   understands. flightId means flightId + price. flightId alone means
   flightWithoutPrice."
  [group-bys]
  (let [group-bys (-> group-bys
                      (disj "device")
                      (disj "bidModifier")
                      (disj "product")
                      (disj "deprecatedProduct"))]
    (cond
      (and (contains? group-bys "flightId")
           (contains? group-bys "price"))
      (disj group-bys "price")

      (contains? group-bys "flightId")
      (-> group-bys
          (disj "flightId")
          (conj "flightWithoutPrice"))

      :else group-bys)))

(defn- redirect-to-index
  []
  (set! js/window.location.hash ""))

(defn- hierarchical-group-by-update
  "Watcher update logic for hierarchical group by elements.
   Ensures that:
     - When the top level is unticked, all children are de-selected
     - When creating a new report and the top level is ticked, the most common children are selected
     - When all children are unticked, the top level is de-selected

   Usage:
     (add-watch group-bys
                :check-for-device
                (hierarchical-group-by-update group-bys \"device\" device-group-bys most-common-device-group-bys))"
  [parent-key children common-children & [deselect-parent?]]
  (fn [_ group-bys old-group-bys new-group-bys]
      ;; de-selects all children dimensions when un-ticking top level checkbox
      (when (and (not (contains? new-group-bys parent-key))
                 (contains? old-group-bys parent-key))
            (reset! group-bys (clojure.set/difference new-group-bys children)))

      ;; selects most commonly used sub-dimensions when ticking top-level checkbox
      (when (and (contains? new-group-bys parent-key)
                 (not (contains? old-group-bys parent-key))
                 (or (false? @scheduled-report-editing?)
                     (and (true? @scheduled-report-editing?)
                          (contains? new-group-bys parent-key)
                          (not (some #(contains? common-children %)
                                     new-group-bys)))))
            (reset! group-bys (clojure.set/union new-group-bys common-children)))
    
      ;; de-selects the top level if you manually de-select the last sub-dimension
      (when (and deselect-parent?
                 (contains? new-group-bys parent-key)
                 (contains? old-group-bys parent-key)
                 (seq (clojure.set/intersection old-group-bys children))
                 (empty? (clojure.set/intersection new-group-bys children)))
        (swap! group-bys disj parent-key))))

(defelem new-report-form
  [_ _]
  (let [{:keys [start-date
                start-datetime
                end-date
                end-datetime
                confirm-future
                date-group
                timezone
                group-bys
                date-range
                report-name
                kickoffdate
                repeats
                window
                other-emails
                fixed-date
                show-revenue
                show-gmv
                show-ivt
                show-conversions
                show-events
                show-bucketing

                ;; Set of vectors like [name {:id id :__title__ title}] where name is a
                ;; string, id is a number or string, and title is a string.
                filters
                exclusion-filters
                date-format] :as state} (reset-form)

        ;; Since this form handles the following three scenarios - editing a
        ;; schedule report, creating a new scheduled report, and generating
        ;; an immediate report. The logical IDs use this prefic to be dynamic
        ;; depending on which form you're using.
        logical-id-prefix (formula-of
                           [scheduled-report-editing? scheduling?]
                            (cond
                              scheduled-report-editing?
                              "edit-scheduled-report-form-"

                              scheduling?
                              "new-scheduled-report-form-"

                              :else
                              "new-immediate-report-form-"))

        parameters         (formula-of [filters]
                                       (mapv (fn [[name {:keys [id]}]]
                                               {name id})
                                             filters))
        exclude-parameters (formula-of [exclusion-filters]
                                       (mapv (fn [[name {:keys [id]}]]
                                               {name id})
                                             exclusion-filters))
        
        exclude-names (formula-of [exclusion-filters]
                                  (mapv (fn [[name {:keys [id]}]]
                                          name)
                                        exclusion-filters))
        
        filter-names (formula-of [filters]
                                 (mapv (fn [[name {:keys [id]}]]
                                         name)
                                       filters))
        
        has-keyword?       (formula-of [group-bys parameters exclude-parameters]
                                       (or (contains? (set group-bys) "keyword")
                                           (boolean (some #{"keyword"} (map ffirst (into parameters exclude-parameters))))))
        request-json       (cell=
                            (let [hourly?     (hourly-reporting? date-group)
                                  start-date  (if hourly? start-datetime start-date)
                                  end-date    (if hourly? end-datetime   end-date)
                                  format-date (if hourly? format-date #(format-date %  "YYYY-MM-DDT00:00:00" {:read-as-utc? false}))] ;; for non-hourly reports, we need to zero out the time section of the date and use the `:read-as-utc? false` option to avoid accidental time zone conversion
                              (merge
                               ;; Timezone doesn't support keyword requests. For UI users,
                               ;; we just show that as a warning.
                               ;; For the api though we need to return an error.
                               ;; That is why we do not merge add the timezone here otherwise,
                               ;; there would be an error returned.
                               (when (and (seq timezone) (not has-keyword?))
                                 {"TimeZone" timezone})
                               (when scheduling?
                                 {"StartDate"          (some-> start-date format-date)
                                  "EndDate"            (some-> end-date format-date)
                                  "RecurrenceType"     repeats
                                  "SchedulingWindow"   window
                                  "Name"               report-name
                                  "Emails"             other-emails
                                  "KickoffDate"        (some-> kickoffdate format-date)
                                  "FixedDate"          fixed-date
                                  "ShowRevenue"        show-revenue
                                  "ShowGMV"            show-gmv
                                  "ShowConversions"    show-conversions
                                  "ShowEvents"         show-events
                                  "ShowClickBucketing" show-bucketing})
                               (when (not scheduling?)
                                 {"Start"                   (some-> start-date format-date)
                                  ;; Here we add one day to the date the user
                                  ;; selected in order to emulate Mullet's
                                  ;; end-date-inclusive behaviour.
                                  ;; This behaviour should NOT happen for new Hourly Reporting
                                 "End"                     (-> (if (not hourly?)
                                                                 (some-> end-date
                                                                         js/moment.
                                                                         (.add 1 "days"))
                                                                 (some-> end-date
                                                                         js/moment.
                                                                         ))
                                                               format-date)
                                  "ReportId"                report-id
                                  :include-click-bucketing? (:clickBucketing rpc/feature-flags)})
                               {"Confirm"           confirm-future
                                "ToDate"            date-range
                                "TimePeriod"        date-group
                                "GroupBy"           (process-group-bys-for-backend group-bys)
                                "Parameters"        parameters
                                "ExcludeParameters" exclude-parameters
                                "ShowIVT"           show-ivt
                                "ReportDateFormat"  date-format})))]

    (add-watch group-bys :group-bys-updated
            (fn [_ _ _ newly]
              (formula-of [timezone filters exclusion-filters exclude-names filter-names]
 
                          (if (and (or (clojure.set/intersection inventory-group-bys newly)
                                       (clojure.set/intersection inventory-group-bys (set exclude-names))
                                       (clojure.set/intersection inventory-group-bys (set filter-names)))
                                   (empty? (clojure.set/intersection performance-group-bys newly))
                                   (empty? (clojure.set/intersection performance-group-bys (set exclude-names)))
                                   (empty? (clojure.set/intersection performance-group-bys (set filter-names)))
                                   (= timezone nil))
                            (reset! report-type "inventory")
 
                            (reset! report-type "performance")))))
    
    (add-watch group-bys
               :check-for-device
               (hierarchical-group-by-update "device" device-group-bys most-common-device-group-bys true))
    
    (add-watch group-bys
               :check-for-flight
               (hierarchical-group-by-update "flightId" #{"price"} #{"price"}))

    (add-watch group-bys
               :check-for-bid-modifier
               (hierarchical-group-by-update "bidModifier" bid-modifier-group-bys most-common-bid-modifier-group-bys true))

    (add-watch
     group-bys :check-for-product
     (fn [k group-bys-ref old-group-bys new-group-bys]
        ;; Default behaviour
        ((hierarchical-group-by-update "product" product-group-bys most-common-product-group-bys true) k group-bys-ref old-group-bys new-group-bys)

       ;; Extra behaviour:
       ;; If `promotedProductSkuId` is already set, there is nothing else to do here.
       ;; If not, then the Promoted Product SKU ID needs to be included when any of the purchased
       ;; products are selected.
       ;; This is because there is an implicit conj of promotedProductSkuId in the backend,
       ;; to ensure promoted/purchased can be sorted by Promoted Product SKU ID.
       ;; We need to wait for the js stack to be empty before adding it so that the input checkbox
       ;; can update it in the DOM, ie: events fully handled so that the when unchecking it
       ;; can be displayed as checked if we had to put it back in the group-bys.
       (when (and
              (not (contains? new-group-bys "promotedProductSkuId"))
              (seq (clojure.set/intersection comm/purchased-product-group-bys new-group-bys)))
         (with-timeout 0
           (reset! group-bys-ref (clojure.set/union new-group-bys #{"promotedProductSkuId"}))))))

    ;; having an on-enter and on-exit isn't sufficient, this do watch is needed here too
    (do-watch on-enter #(reset-form @form-cells))

    (add-watch
     group-bys :check-for-deprecated-product
     (hierarchical-group-by-update "deprecatedProduct" deprecated-product-group-bys most-common-deprecated-product-group-bys true))
    
    ;; having an on-enter and on-exit isn't sufficient, this do watch is needed here too
    (do-watch on-enter #(reset-form @form-cells))

    [(component/titlebar
      :region (rpc/from-lang-pack ::new-report-form-component-titlebar-region)
      :zone (if @scheduled-report-editing?
              (rpc/from-lang-pack ::edit-scheduled-report-form-component-titlebar-zone)
              (rpc/from-lang-pack ::new-report-form-component-titlebar-zone)))
     (div
      :class "new-report-form"
      :adzerk-logical-id (cell= (str logical-id-prefix "form"))
      :css   {:display        "flex"
              :flex-wrap      "wrap"
              :margin         "5px"
              ;; Keeps the always-on-top bar at the bottom of the form from
              ;; obscuring the last part of the content in the form.
              :padding-bottom "150px"}

      (panel
       :title (rpc/from-lang-pack ::new-report-form-panel-dates-title)
       :css {:min-width    "310px"
             :max-width    "310px"
             :margin-right "10px"
             :margin-top   "10px"}
       (date-fields-picker
        :date-range date-range
        :start-date start-date
        :start-datetime start-datetime
        :confirm confirm-future
        :end-date end-date
        :end-datetime end-datetime
        :date-group date-group
        :timezone timezone)

       (div
        (hoplon.core/label
         :for "date-format"
         (rpc/from-lang-pack ::date-fields-report-date-format-label))
        (div
         :class "select"
         (input/select
          :id "date-format"
          :state date-format
          :class "form-control"
          (option :selected (cell= (= date-format "us")) :value "us" (rpc/from-lang-pack ::date-fields-report-date-format-option-us))
          (option :selected (cell= (= date-format "iso8601")) :value "iso8601" (rpc/from-lang-pack ::date-fields-report-date-format-option-iso8601))))))

      (panel
       :title (rpc/from-lang-pack ::new-report-form-panel-groupby-title)
       :css {:min-width    "310px"
             :width        "500px"
             :max-width    "500px"
             :margin-right "10px"
             :margin-top   "10px"}
       :content-css {:padding "20px"}
       (div :class "report-grid"
        (div 
         (rpc/from-lang-pack ::report-parameters-group-by-report-type)
         (if-tpl (cell= (= report-type "inventory"))
          (span :css {:font-weight "bold" :margin-left "5px"} (rpc/from-lang-pack ::report-parameters-group-by-report-type-inventory))
          (span :css {:font-weight "bold" :margin-left "5px"} (rpc/from-lang-pack ::report-parameters-group-by-report-type-performance))))
        (grid/grid
         :css {:margin-top "10px"}
        (grid/row
         (grid/item
          (panel
           :title (rpc/from-lang-pack ::report-parameters-group-by-inventory)
           :panel-css {:height "100%"}
           :content-css {:padding "0px 16px 16px"}
           (group-bys-picker-inventory :group-bys group-bys)))
         (grid/item
          (panel
           :title (rpc/from-lang-pack ::report-parameters-group-by-performance) 
           :panel-css {:height "100%"}
           :content-css {:padding "0px 16px 16px"}
           (group-bys-picker-performance :group-bys group-bys)))))))

      (panel
       :title (rpc/from-lang-pack ::new-report-form-panel-filters-title)
       :css {:min-width    "500px"
             :width        "500px"
             :max-width    "600px"
             :margin-right "10px"
             :margin-top   "10px"
             :flex         1}
       :content-css {:padding-top "20px"}
       (filter-picker :state filters))

      (when-tpl (cell= (:reportingExcludeParameters rpc/feature-flags))
        (panel
         :title (rpc/from-lang-pack ::new-report-form-panel-exclusion-filters-title)
         :css {:min-width  "500px"
               :width      "500px"
               :max-width  "600px"
               :margin-top "10px"
               :flex       1}
         :content-css {:padding-top "20px"}
         (filter-picker :state exclusion-filters :exclusion-filters? true)))

      (form/alert-warning
       :css {:width "100%"}
         :toggle
         (formula-of [timezone group-bys parameters scheduling? has-keyword?]
                     ;; There is already a warning in scheduled reporting,
                     ;; so no reason to duplicate.
                     (and (not scheduling?)
                          has-keyword?
                          timezone
                          (not (contains? #{"utc"} timezone))))
         ;; Warns you that timezone is not support in keyword reporting
         (rpc/from-lang-pack ::timezones-keyword-reporting-warning))

      (panel :toggle scheduling?
             :adzerk-logical-id (cell= (str logical-id-prefix "scheduling-panel"))
             :title (rpc/from-lang-pack ::new-report-form-panel-scheduling-title)
             :css    {:min-width    "1120px"
                      :width        "1120px"
                      :max-width    "1545px"
                      :margin-right "10px"
                      :margin-top   "10px"}
        (grid/grid
          (grid/row
            (grid/item (h1 (rpc/from-lang-pack ::new-report-form-panel-scheduling-h1))))
          (grid/row
            (grid/item (h3 (rpc/from-lang-pack ::new-report-form-panel-scheduling-h3)))))
        (grid/grid
          (grid/row
            (grid/item
              (hoplon.core/label
                :for "report-name"
                (rpc/from-lang-pack ::new-report-form-panel-scheduling-report-name)
                (em " (optional)"))
              (form/text-input
                :id "report-name"
                :state report-name))
            (grid/item
              (hoplon.core/label
                :for "kickoff-date"
                (rpc/from-lang-pack ::new-report-form-panel-scheduling-kickoff-date))
              (form/date-input
                :id "kickoff-date"
                :state kickoffdate))))
        (grid/grid
          (grid/row
            (grid/item
              (hoplon.core/label
                :for "repeats"
                (rpc/from-lang-pack ::new-report-form-panel-scheduling-repeats))
              (form/radio-block :name "repeats" :state repeats :value const/REPORT-REPEAT-NEVER
                                (rpc/from-lang-pack (get const/repeat-type const/REPORT-REPEAT-NEVER)))
              (form/radio-block :name "repeats" :state repeats :value const/REPORT-REPEAT-DAILY
                                (rpc/from-lang-pack (get const/repeat-type const/REPORT-REPEAT-DAILY)))
              (form/radio-block :name "repeats" :state repeats :value const/REPORT-REPEAT-WEEKLY
                                (rpc/from-lang-pack (get const/repeat-type const/REPORT-REPEAT-WEEKLY)))
              (form/radio-block :name "repeats" :state repeats :value const/REPORT-REPEAT-MONTHLY
                                (rpc/from-lang-pack (get const/repeat-type const/REPORT-REPEAT-MONTHLY))))
            (grid/item
              (hoplon.core/label
                :for "window"
                (rpc/from-lang-pack ::new-report-form-panel-scheduling-window))
              (form/radio-block :name "window" :state window :value const/REPORT-RUN-FIRST
                                (rpc/from-lang-pack (get const/run-type const/REPORT-RUN-FIRST)))
              (form/radio-block :name "window" :state window :value const/REPORT-RUN-SECOND
                                (rpc/from-lang-pack (get const/run-type const/REPORT-RUN-SECOND)))
              (form/radio-block :name "window" :state window :value const/REPORT-RUN-THIRD
                                (rpc/from-lang-pack (get const/run-type const/REPORT-RUN-THIRD)))
              (form/radio-block :name "window" :state window :value const/REPORT-RUN-FOURTH
                                (rpc/from-lang-pack (get const/run-type const/REPORT-RUN-FOURTH))))))

        (grid/grid
          (grid/row
           (grid/item
            (form/checkbox-block
             :name "fixed-date"
             :state fixed-date
             :value true
             (rpc/from-lang-pack ::new-report-form-panel-scheduling-fixed-date)))
           (grid/item
            (form/checkbox-block
             :id "show-revenue"
             :state show-revenue
             :value true
             (rpc/from-lang-pack ::new-report-form-panel-scheduling-show-revenue)))
           (grid/item
            (form/checkbox-block
             :id "show-gmv"
             :state show-gmv
             :value true
             (rpc/from-lang-pack ::new-report-form-panel-scheduling-show-gmv)))
           (when (:unfilteredMetricsEnabled @rpc/feature-flags)
             (grid/item
              (form/checkbox-block
               :id "show-ivt"
               :state show-ivt
               :value true
               (rpc/from-lang-pack ::new-report-form-panel-scheduling-show-ivt))))
           (grid/item
            (form/checkbox-block
             :id "show-conversions"
             :state show-conversions
             :value true
             (rpc/from-lang-pack ::new-report-form-panel-scheduling-show-conversions)))
           (grid/item
                :toggle (formula-of [group-bys]
                         (let [display-show-events (empty? (clojure.set/intersection
                                                            comm/purchased-product-group-bys
                                                            (set group-bys)))]
                           display-show-events))
            (form/checkbox-block
             :id   "show-events"
             :state show-events
                 :value true
                 (rpc/from-lang-pack ::new-report-form-panel-scheduling-show-events)))
           (grid/item
            (form/checkbox-block
             :id "show-bucketing"
             :state show-bucketing
             :value true
             (rpc/from-lang-pack ::new-report-form-panel-scheduling-show-bucketing)))))

        (grid/grid
          (grid/row
            (grid/item
              (hoplon.core/label
                :for "other-emails"
               (rpc/from-lang-pack ::new-report-form-panel-scheduling-other-emails)
               (em (rpc/from-lang-pack ::new-report-form-panel-scheduling-other-emails-optional)))
              (span :class "muted" (rpc/from-lang-pack ::new-report-form-panel-scheduling-other-emails-extra))
              (form/textarea
                :id "other-emails"
                :state other-emails)))))

      (div
       :class "report-footer"
       (let [submitting? (cell= (= state :report-submitting))]
         (button/primary
       ;  :css {:display "inline-block"}
          :adzerk-logical-id (cell= (str logical-id-prefix "submit-button"))
          :disabled submitting?
          :text  (cell=
                   (cond
                     (and submitting? scheduling?)
                     ~(rpc/from-lang-pack ::new-report-form-button-action-scheduling)

                     (and submitting? (not scheduling?))
                     ~(rpc/from-lang-pack ::new-report-form-button-action-generating)

                     (and (not submitting?) (not scheduling?))
                     ~(rpc/from-lang-pack ::new-report-form-button-action-generate)

                      (and (not submitting?) scheduling? scheduled-report-editing?)
                      ~(rpc/from-lang-pack ::new-report-form-button-action-edit-schedule)

                     (and (not submitting?) scheduling?)
                     ~(rpc/from-lang-pack ::new-report-form-button-action-schedule)))
          :click #(event! :submit-request @request-json)))

       (button/secondary
        :css {:display     "inline-block"
              :margin-left "7px"}
         :disabled  (cell= (= state :report-submitting))
         :click #(dosync
                   (reset! report-meta nil)
                   (reset-form @form-cells))
         (rpc/from-lang-pack ::new-report-form-button-reset))
       (button/secondary
        :css {:display     "inline-block"
              :margin-left "7px"}
        :click #(dosync
                 (reset! report-meta nil)
                 (reset-form @form-cells)
                 (redirect-to-index))
         (rpc/from-lang-pack ::new-report-form-button-cancel))
       (button/secondary
         :class "pull-right"
         :css   {;; 2023-03-01 Dave: We introduced this in November 2021 to
                 ;; prevent the button from overlapping with a Pendo button. We
                 ;; don't use Pendo anymore, so we can consider removing this
                 ;; now.
                 :margin-right "4em"}
         :click #(dosync
                  (swap! scheduling? not)
                  (when @scheduled-report-editing?
                    (reset! scheduled-report-editing? false)))
         :text (cell= (if scheduling?
                        ~(rpc/from-lang-pack ::new-report-form-button-change-to-generate)
                        ~(rpc/from-lang-pack ::new-report-form-button-change-to-schedule))))
       (div
         :toggle report-submit-failed?
         :adzerk-logical-class "report-submit-error"
         :class  "report-submit-error"
         :css    (cell= {:display     (if report-submit-failed?
                                        "inline-block"
                                        "none")
                         :margin-left "7px"})
         (span
           :toggle error-message
           :text   (rpc/from-lang-pack
                    ::new-report-form-error-specific
                    (cell= {:error-message error-message})))
         (span
           :toggle (cell= (not error-message))
           (rpc/from-lang-pack ::new-report-form-error-general)
           (a :href "mailto:support@kevel.co"
              (rpc/from-lang-pack ::new-report-form-error-general-2))
           "."))))
     (dev/pre-cell "Request" request-json)]))

;;; Existing report display

(def report-field-titles-from-text
  (rpc/from-lang-pack ::report-field-titles-from))

(def report-field-titles
  (formula-of [report-meta report-field-titles-from-text report-schema]
    (let [report-time-period (get-in report-meta ["Request" "TimePeriod"])]
      (-> (for [[field {:keys [title]}] report-schema]
            [field title])
          (->> (into {}))
          (assoc "start"
                 ;; We used to vary these by the time period, but I
                 ;; think it makes more sense to just be consistent.
                 ;; TODO: Remove this entirely, as it's no longer doing
                 ;; anything useful
                 (get {"hour"  report-field-titles-from-text
                       "day"   report-field-titles-from-text
                       "week"  report-field-titles-from-text
                       "month" report-field-titles-from-text
                       "total" report-field-titles-from-text}
                      report-time-period))))))

(def report-field-titles-tooltips
  {:fillrate             {:title (rpc/from-lang-pack ::report-fields-tooltip-fillrate)}
   :requestcount         {:title (rpc/from-lang-pack ::report-fields-tooltip-requestcount)}
   :decisionrequestcount {:title   (rpc/from-lang-pack ::report-fields-tooltip-decisionrequestcount)
                          :subtext (rpc/from-lang-pack ::report-fields-tooltip-decisionrequestcountsubtext)
                          :link    (cell= (str "#!/" rpc/networkid "/ad-decision-requests"))}
   :end                  {:title (rpc/from-lang-pack ::csv-specific-fields-end-title-tooltip)}
   :start                {:title (rpc/from-lang-pack ::report-field-titles-from-tooltip)}})

(defelem sidebar-table
  "Given a seq of label-value pairs, returns UI for the sidebar table formats"
  [attrs _]
  (with-attr-bindings attrs [items]
    (let [item-css {:margin-bottom "1em"}
          label-css {:font-weight "bold"}
          value-css {}]
      (div
       attrs
       (for [[label value] items]
         (div :css item-css
              (div :css label-css label)
              (div :css value-css value)))))))

(defelem clipboard-link
  [attrs _]
  (let [target-id           (str (gensym))
        trigger-id          (str (gensym))
        show-copy-msg       (cell false)]
    [ (button/primary :toggle false :id trigger-id :data-clipboard-target (str "#" target-id))
      (with-let [elem     (div
                            (a (assoc attrs :id target-id :click #(.click (jquerying (str "#" trigger-id)))))
                            (p :css {:margin-left "10px"} :toggle show-copy-msg "Copied!"))]
        (let [clippy  (new clipboard (str "#" trigger-id))]
          (.on clippy "success" (fn [_]
                                  (reset! show-copy-msg true)
                                  (with-timeout 2500
                                    (reset! show-copy-msg false))))))]))

(defn- report-parameters
  "Returns UI for the parameters section of the report summary."
  [report-meta]
  (sidebar-table
   :css {:border-bottom "solid 1px #eee"}
   :items (as-> [[(rpc/from-lang-pack ::report-parameters-report)
                  (formula-of [report-meta]
                              (let [repeat-type (cell= (get-in report-meta ["Request" "Schedule" "RecurrenceType"]))
                                    report-id   (cell= (get-in report-meta ["Request" "ReportId"]))
                                    first-line  (cell=
                                                 (or
                                                  (get-in report-meta ["Request" "Schedule" "Name"])
                                                  (when-let [owner (get-in report-meta ["Request" "Schedule" "Owner"])]
                                                    (str
                                                     owner
                                                     " "
                                                     (if (=  repeat-type 0)
                                                       "Ad Hoc"
                                                       (get const/repeat-type repeat-type))))
                                                  report-id))
                                    second-line (cell= (when-not (= first-line report-id) report-id))]
                                (div
                                 (clipboard-link
                                  :data-clipboard-text (cell= report-id)
                                  :text (cell= report-id)
                                  :css {:color "inherit" :margin-left 0}
                                  :href (cell= (str "#!/" rpc/networkid  "/reports/" report-id))
                                  :toggle (cell= (not second-line)))
                                 (span :toggle second-line :text first-line)
                                 (clipboard-link
                                  :data-clipboard-text (cell= report-id)
                                  :text (cell= report-id)
                                  :class "help-text"
                                  :css {:color "inherit"}
                                  :href (cell= (str "#!/" rpc/networkid  "/reports/" report-id))
                                  :toggle second-line))))]

                 [(rpc/from-lang-pack ::report-parameters-schedule)
                  (formula-of [report-meta]
                              (let [repeat-type (get-in report-meta ["Request" "Schedule" "RecurrenceType"])
                                    first-line  (or
                                                 (get-in report-meta ["Request" "Schedule" "Name"])
                                                 (when-let [owner (get-in report-meta ["Request" "Schedule" "Owner"])]
                                                   (str
                                                    owner
                                                    " "
                                                    (if (=  repeat-type 0)
                                                      "Ad Hoc"
                                                      (get const/repeat-type repeat-type))))
                                                 (rpc/from-lang-pack ::report-parameters-schedule-make))]
                                (div (a :href (cell= (str "#!/" rpc/networkid "/reports/scheduled"))
                                        :css {:color "inherit"}
                                        :click #(dosync
                                                 (reset! scheduling? true)
                                                 (reset! copying? true))
                                        (em :text (cell= first-line))))))]
                 [(rpc/from-lang-pack ::timezone-label)
                  (formula-of [report-meta]
                              (get-in report-meta ["Request" "TimeZone"] "GMT"))]

                 [(rpc/from-lang-pack ::report-parameters-start-date)
                  (formula-of [report-meta]
                              (let [start-date (get-in report-meta ["Request" "Start"])
                                    ;; date formatting including time is required for hourly reporting
                                    dt-format  (if (hourly-reporting? (get-in report-meta ["Request" "TimePeriod"]))
                                                 "YYYY-MMM-DD HH:mm"
                                                 "YYYY-MMM-DD")]
                                (format-date start-date dt-format)))]

                 ;; When we submitted the report, we added one day to the provided end
                 ;; date so that we could present a UI that asks the user for an
                 ;; inclusive end date, but use a backend that expects an exclusive
                 ;; end date.
                 ;;
                 ;; When we get the report back, the metadata includes the exclusive
                 ;; end date we asked for, so to maintain the illusion that we're
                 ;; working with an inclusive end date, we subtract the day we added.
                 [(rpc/from-lang-pack ::report-parameters-end-date)
                  (formula-of [report-meta]
                              (let [time-periods  (if (get-in report-meta ["Request" "Schedule"]) 0 1)
                                    end-date      (get-in report-meta ["Request" "End"])
                                    hourly-report (hourly-reporting? (get-in report-meta ["Request" "TimePeriod"]))]
                                ;; turns out for hourly reporting we need to subtract 1 hour, for other reports 1 day
                                (if hourly-report
                                  (-> (util/as-utc end-date)
                                      (util/hours-ago  time-periods)
                                      (format-date  "YYYY-MMM-DD HH:mm"))
                                  (-> (util/days-ago end-date time-periods)
                                      (format-date "YYYY-MMM-DD")))))]
                 [(rpc/from-lang-pack ::report-parameters-date-group)
                  (formula-of [report-meta]
                              (-> report-meta
                                  (get-in ["Request" "TimePeriod"])
                                  {"hour"  @(rpc/from-lang-pack ::report-parameters-date-group-hour)
                                   "day"   @(rpc/from-lang-pack ::report-parameters-date-group-day)
                                   "week"  @(rpc/from-lang-pack ::report-parameters-date-group-week)
                                   "month" @(rpc/from-lang-pack ::report-parameters-date-group-month)
                                   "total" @(rpc/from-lang-pack ::report-parameters-date-group-total)}))]
                 [(rpc/from-lang-pack ::report-parameters-group-by)
                  (let [report-params-group-by-none-text
                        (rpc/from-lang-pack ::report-parameters-group-by-none)]
                    (formula-of [report-meta report-params-group-by-none-text request-schema]
                                (if-let [groupings (seq (get-in report-meta ["Request" "GroupBy"]))]
                                  (->> groupings
                                       ;; TODO if a groupings contains a key
                                       ;; that does not exist in the
                                       ;; request-schema. i.e: hoenir returned
                                       ;; it. then the page will fail to load.
                                       ;; We need to recover from this and
                                       ;; ignore groupings that are not in the
                                       ;; schema.
                                       (map #(get-in request-schema [% :title]))
                                       (interpose ", ")
                                       (apply str))
                                  report-params-group-by-none-text)))]
                 [(rpc/from-lang-pack ::report-parameters-filters)
                  (let [filters (formula-of
                                 [report-meta request-schema]
                                 (->> (get-in report-meta ["Request" "Parameters"])
                                      (map (fn [{:keys [field value title] :as filter}]
                                             (assoc filter :label (get-in request-schema [field :title])
                                                           :value (or title value))))))]
                    (if-tpl (cell= (empty? filters))
                      (span (rpc/from-lang-pack ::report-parameters-filters-none))
                      (ul (for-tpl
                           [{:keys [label value]} filters]
                           (li (text "~{label} = ~{value}"))))))]] items

            (if (:reportingExcludeParameters @rpc/feature-flags)
              (conj items [(rpc/from-lang-pack ::report-exclusion-parameters-filters)
                           (let [exclusion-filters (formula-of
                                                    [report-meta request-schema]
                                                    (->> (get-in report-meta ["Request" "ExcludeParameters"])
                                                         (map (fn [{:keys [field value title] :as exclusion-filter}]
                                                                (assoc exclusion-filter
                                                                  :label (get-in request-schema [field :title])
                                                                  :value (or title value))))))]
                             (if-tpl (cell= (empty? exclusion-filters))
                               (span (rpc/from-lang-pack ::report-exclusion-parameters-filters-none))
                               (ul (for-tpl
                                    [{:keys [label value]} exclusion-filters]
                                    (li (text "~{label} = ~{value}"))))))])
              items))))

(defn- report-summary-stats
  "Returns UI for the statistics section of the report summary."
  [summary]
  (let [elem (div
              (div :css {:font-weight   "bold"
                         :margin-bottom "1em"}
                   @(rpc/from-lang-pack ::report-summary-stats-totals))
              (for [field ["impressions" "clicks" "ctr" "revenue"]]
                (div
                 :css {:margin-bottom "0.25em"}
                 (span
                  :css {:font-weight  "bold"
                        :margin-right "0.5em"
                        :width        (util/px 85)
                        :display      "inline-block"}
                  (formula-of [report-field-titles]
                    (get report-field-titles field)))
                 (span
                  :css {:margin-right "0.5em"}
                  (formula-of [summary report-field-formats-fn]
                    (format-field-value summary field report-field-formats-fn {:mode :summary}))))))]
    elem))

(defn download-csv
  "Causes a navigation event to download the Hoenir report CSV"
  []
  (-> js/window
      .-location
      (set! (str rpc/ADZERK_GEIR_BACKEND_URL "/report-download/" @report-id "?networkid=" @rpc/networkid))))


(defn edit-scheduled-report
      [report-id]
      (fetch-scheduled-report report-id)
      (reset! state :edit-request)
      (reset! scheduling? true)
      (reset! scheduled-report-editing? true)
      (reset! report-id-to-delete report-id)
      (let [hash (str "#!/" @rpc/networkid "/reports/scheduled")]
           (-> js/window
               .-location
               .-hash
               (set! hash))))

(defn edit-report
  []
  (reset! copying? true)
  (when (get-in @report-meta ["Request" "Schedule"]) (reset! scheduling? true))
  ;; We use a virtual checkboxes for parent groups, when we submit a report
  ;; with such grouping we remove the parent field from the groupby because it
  ;; is not valid.
  ;; When we edit the report we need to put them back in the GroupBy if any
  ;; child grouping is selected.
  (let [virtual-fields [{:parent "device" :children device-group-bys}
                        {:parent "bidModifier" :children bid-modifier-group-bys}
                        {:parent "product" :children product-group-bys}
                        {:parent "deprecatedProduct" :children deprecated-product-group-bys}]]
    (doseq [virtual-field virtual-fields]
      (when (seq
              (clojure.set/intersection (set (get-in @report-meta ["Request" "GroupBy"]))
                                        (:children virtual-field)))
        (swap! report-meta update-in ["Request" "GroupBy"] conj (:parent virtual-field))))
  )

  (let [hash      (str "#!/" @rpc/networkid (if @scheduling? "/reports/scheduled" "/reports/new"))]
    (-> js/window
        .-location
        .-hash
        (set! hash))))

(defn- report-summary
  "Returns UI for the report summary."
  [summary report-meta]
  (vector
   (div
    :debug "parameters"
    :css {:flex 1
          :padding "2em"}
    (report-parameters report-meta))
   (div
    :debug "stats"
    :css {:border-top "solid 1px black"
          :border-bottom "solid 1px black"
          :padding "1em 2em 1em"}
    (report-summary-stats summary))
   (button/primary
    :css {:margin "1em auto"
          :display "inline"
          :width "50%"}
    :text @(rpc/from-lang-pack ::report-summary-button-edit-text)
    :click edit-report)
   (button/secondary
    :css {:margin "1em auto"
          :display "inline"
          :width "50%"}
    :text @(rpc/from-lang-pack ::report-summary-button-download-text)
    :click download-csv)))

(defn- format-graph-date
  "Turns milliseconds into a date string. Deals with the nonsense
  around midnight and UTC and stuff."
  [ms]
  (-> (js/moment ms)
      (.format "YYYY-MMM-DD HH:mm")))

(def ^:private graph-tabs
  "Describes the tabs in the graph panel. Keys are:

  `:title` - The text shown at the top of the chart and on the y axis.
  `:type`  - The Highcharts chart type. E.g. `:area`.
  `:series` - The field on the detail rows that we'll be graphing.
  `:formatters` - A map of keys to functions for formatting the data.
  See below.

  Formatters are as follows:

  `:y-axis` - A function of one argument - a value - to a value to
  display on the y axis.
  `:tooltip` - A function from a map with keys `:x` and `:y` to a
  value to display when hovering over a data point. Supports a limited
  subset of HTML."
  {:impressions {:title      (rpc/from-lang-pack ::graph-tabs-impressions-title)
                 :type       :area
                 :series     "impressions"
                 :formatters {:y-axis  #(-> % (/ 1000) (str "K"))
                              :tooltip (fn [{:keys [x y]}]
                                         (str "<b>"
                                              y
                                              " impressions</b> on "
                                              (format-graph-date x)))}}
   :clicks      {:title      (rpc/from-lang-pack ::graph-tabs-clicks-title)
                 :type       :area
                 :series     "clicks"
                 :formatters {:y-axis  str
                              :tooltip (fn [{:keys [x y]}]
                                         (str "<b>"
                                              y
                                              " clicks</b> on "
                                              (format-graph-date x)))}}
   :ctr         {:title      (rpc/from-lang-pack ::graph-tabs-ctr-title)
                 :type       :line
                 :series     "ctr"
                 :formatters {:y-axis  #(format-percent* % 2)
                              :tooltip (fn [{:keys [x y]}]
                                         (str "<b>"
                                              (format-percent* y 2)
                                              "</b> on "
                                              (format-graph-date x)))}}
   :cvrclicks   {:title      (rpc/from-lang-pack ::graph-tabs-cvrclicks-title)
                 :type       :line
                 :series     "cvrclicks"
                 :formatters {:y-axis  #(format-percent* % 2)
                              :tooltip (fn [{:keys [x y]}]
                                         (str "<b>"
                                              (format-percent* y 2)
                                              "</b> on "
                                              (format-graph-date x)))}}
   :cvrimpressions {:title      (rpc/from-lang-pack ::graph-tabs-cvrimpressions-title)
                    :type       :line
                    :series     "cvrimpressions"
                    :formatters {:y-axis  #(format-percent* % 2)
                                 :tooltip (fn [{:keys [x y]}]
                                            (str "<b>"
                                                 (format-percent* y 2)
                                                 "</b> on "
                                                 (format-graph-date x)))}}
   :revenue     {:title      (rpc/from-lang-pack ::graph-tabs-revenue-title)
                 :type       :line
                 :series     "revenue"
                 :formatters {:y-axis  #(format-revenue %)
                              :tooltip (fn [{:keys [x y]}]
                                               (str "<b>"
                                                    (format-revenue y)
                                                    "</b> on "
                                                    (format-graph-date x)))}}
   :ecpm        {:title      (rpc/from-lang-pack ::graph-tabs-ecpm-title)
                 :type       :line
                 :series     "ecpm"
                 :formatters {:y-axis  #(format-revenue %)
                              :tooltip (fn [{:keys [x y]}]
                                            (str "<b>"
                                                 (format-revenue y)
                                                 "</b> on "
                                                 (format-graph-date x)))}}
   :cpc         {:title      (rpc/from-lang-pack ::graph-tabs-cpc-title)
                 :type       :line
                 :series     "cpc"
                 :formatters {:y-axis  #(format-revenue %)
                              :tooltip (fn [{:keys [x y]}]
                                           (str "<b>"
                                                (format-revenue y)
                                                "</b> on "
                                                (format-graph-date x)))}}})

(defn- graphs
  "Returns UI for the report graphs."
  [rollup-rows height]
  (let [border-width 2]
   (div
    :css {:height           (- height border-width)
          :border-top-width (util/px border-width)
          :border-top-color "#eee"
          :border-top-style "solid"}
    (if-tpl (cell= (= "total" report-time-period))
      @(rpc/from-lang-pack ::graphs-not-available)
      (let [active-tab (cell :impressions)]
        (vector
         ;; Header with tabs
         (div
          :class "tabs report-graph-tabs"
          (ul
           :class ["nav" "nav-tabs"]
           (for [[id {:keys [title]}] graph-tabs]
             (li
              :class (cell= {:active (= id active-tab)})
              (a :click #(reset! active-tab id)
                 title)))))
         (let [graph-padding 26]
           (with-dims {:t top}
             [chart (div)]
             (let [chart-div-height (cell= (- height top border-width))]
               (chart :css (cell= {:height  chart-div-height
                                   :padding (util/px graph-padding)})
                      (highcharts/chart :chart (formula-of [active-tab chart-div-height]
                                                 {:type   (-> active-tab graph-tabs :type)
                                                  :height (- chart-div-height (* 2 graph-padding))})
                                        :legend {:enabled false}
                                        :credits {:enabled false}
                                        :title {:text ""}
                                        :subtitle {:text ""}
                                        :tooltip (formula-of [active-tab]
                                                   {:formatter (let [formatter (-> active-tab graph-tabs :formatters :tooltip)]
                                                                 (fn []
                                                                   (this-as this
                                                                     (formatter {:x (.-x this)
                                                                                 :y (.-y this)}))))})

                                        :xAxis (formula-of [report-meta]
                                                 {:type   "datetime"
                                                  ;; I can't quite convince myself that we need to
                                                  ;; pin the graphs to the entire span of the
                                                  ;; report time - it doesn't look quite right to
                                                  ;; me. But I don't want to throw the code away
                                                  ;; because it was a PITA.
                                                  ;; :min    (-> report-meta
                                                  ;;             (get-in ["Request" "Start"])
                                                  ;;             (js/moment "YYYY-MM-DD")
                                                  ;;             .utc
                                                  ;;             .valueOf)
                                                  ;; :max    (-> report-meta
                                                  ;;             (get-in ["Request" "End"])
                                                  ;;             (js/moment "YYYY-MM-DD")
                                                  ;;             .utc
                                                  ;;             .valueOf)
                                                  :labels {:formatter (fn []
                                                                        (this-as this
                                                                          (-> this .-value format-graph-date)))}})
                                        :yAxis (formula-of [active-tab]
                                                 {:title  {:text @(-> active-tab graph-tabs :title)}
                                                  :labels {:formatter (let [formatter (-> active-tab graph-tabs :formatters :y-axis)]
                                                                        (fn []
                                                                          (this-as this
                                                                            (-> this .-value formatter))))}})
                                        :plotOptions {:area {:market {:enabled false
                                                                      :symbol  "circle"
                                                                      :radius  2
                                                                      :states  {:hover {:enabled true}}}}}
                                        :series (formula-of [rollup-rows active-tab]
                                                  (let [{:keys [series]} (graph-tabs active-tab)]
                                                    [{:data (->> (for [{:keys [rollup]} rollup-rows]
                                                                   (let [x (-> (get rollup "start")
                                                                               (js/moment "YYYY-MM-DD HH:mm:ssZ")
                                                                               .valueOf)
                                                                         y (get rollup series)]
                                                                     [x y]))
                                                                 (sort-by first))}])))))))))))))

(def show-events?
  (local-storage
   (cell (if (get-in @report-meta ["Request" "AdzerkMeta" "TotalsSubtotals?"])
           false
           true))
   (str ::show-events?)))

(def show-revenue?
  (local-storage
   (cell true)
   (str ::show-revenue?)))

(def show-gmv?
  (local-storage
   (cell true)
   (str ::show-gmv?)))

(def show-ivt?
  (local-storage
   (cell false)
   (str ::show-ivt?)))

(def max-report-length
  "The maximum length of a report (in bytes) the server will give us
  before returning a 'too big' error. We do this to prevent the
  browser attempting to render a report that would take forever to
  draw."
  (local-storage
   (cell 150000)
   (str ::max-report-length)))

(defn- compute-report-data
  []
  ;; We should probably move this stuff into either the rpc namespace or a
  ;; namespace of its own.
  (let [id @report-id]
    (reset! report-data ::computing)
    ;; TODO: I would like the retrieval to happen in the worker
    ;; thread as well, since it would prevent us handling large
    ;; strings in the UI thread, but I'm concerned that getting
    ;; the Castra stuff right over there might be difficult.
    (-> {:url         (str rpc/ADZERK_GEIR_BACKEND_URL
                           "/report-get/" id
                           "?max-report-length=" @max-report-length)
         :headers     {"X-Castra-Csrf" true
                       "X-Kevel-NetworkId" @rpc/networkid}
         :credentials true}
        castra.core/ajax-fn
        (.done (fn [{:keys [body]}]

                 (worker-call :compute
                              {:csv body
                               :report-meta @report-meta
                               :feature-flags @rpc/feature-flags}
                               ;; TODO: More

                              #(with-time "render report"
                                 (event! :report-computed %))
                              #(event! :report-computation-error %))))
        (.fail (fn [{:keys [body status status-text]}]
                 (let [{:keys [reason]} (try (transit/decode body) (catch :default _))]
                   (if (= reason :geir-backend.rpc/report-too-big)
                     (event! :report-too-big)
                     (event! :report-csv-retrieval-error
                             {:reason      :request-error
                              :status      status
                              :status-text status-text
                              :body        body}))))))))

(defelem existing-report
  [{:keys [report-too-big]} _]
  (vector
   (component/titlebar
    :region (a :href (cell= (str "#!/" rpc/networkid "/reports/new")) (i :icon "left") "Reports")
    :zone ""
    (for [[state label]
          (cond->> [[show-revenue? (rpc/from-lang-pack ::existing-report-show-revenue)]
                   [show-gmv? (rpc/from-lang-pack ::existing-report-show-gmv)]]

            (:unfilteredMetricsEnabled @rpc/feature-flags)
            (cons [show-ivt? (rpc/from-lang-pack ::existing-report-show-ivt)])

            (nil? (get-in @report-meta ["Request" "AdzerkMeta" "TotalsSubtotals?"]))
            (cons [show-events? (rpc/from-lang-pack ::existing-report-show-events)]))]
      (div :class "checkbox"
           :css {:margin  "10px"
                 :display "inline-block"}
           (hoplon.core/label {}
                              (span :class "report-switch"
                                    (input/checkbox :state state)
                                    (i))
                              label))))
   (let [summary-width 320
         graph-height  250]
     (cell-let [{:keys [summary report-meta]} report-data]
       (with-dims {:t top-y}
         [report-area (div)]
         (report-area
          :css {:display "flex"}
          ;; summary pane
          (div
           :debug "summary"
           :css (formula-of [window-size top-y]
                  (let [[_ wh] window-size]
                    {:width          (util/px summary-width)
                     ;; Need this too to get Firefox to respect the width
                     :min-width      (util/px summary-width)
                     :height         (util/px (- wh top-y))
                     :background     "#eee"
                     :display        "flex"
                     :flex-direction "column"}))
           (report-summary summary report-meta))
          ;; content pane
          (div
           :debug "content"
           :css (formula-of [window-size]
                  (let [[ww _] window-size]
                    {:width     (util/px (- ww summary-width))
                     ;; Need this too to get Firefox to respect the width
                     :max-width (util/px (- ww summary-width))
                     :flex      1}))
           ;; Breakdown
           (div
            :debug "breakdown"
            :css (formula-of [window-size top-y]
                   (let [[_ wh] window-size]
                     {:height   (util/px (- wh top-y graph-height))
                      :display (when report-too-big "flex")
                      :justify-content (when report-too-big "center")
                      :align-items (when report-too-big "center")}))

            (if report-too-big
              (div :css {:width "600px"}
                   (h2 @report-too-big-detail)
                   (p :css {:margin "0"} @report-too-big-remediation1)
                   (button/primary
                    :class "download-csv report-error-button"
                    :text  @report-too-big-download
                    :click download-csv)
                   (p :css {:margin-top "1.5rem"} @report-too-big-remediation2))
              (helix-hoplon/mount-react
                new-table/report-table
                {:show-events? show-events?
                 :show-revenue? show-revenue?
                 :show-gmv? show-gmv?
                 :show-ivt? (if (:unfilteredMetricsEnabled @rpc/feature-flags)
                              show-ivt?
                              true)
                 :show-impression-bucketing? (:impressionBucketing @rpc/feature-flags)
                 :non-event-fields (cell= (set (keys report-schema)))
                 :report-data report-data
                 :report-field-titles report-field-titles
                 :report-field-titles-tooltips report-field-titles-tooltips
                 :report-field-formats-fn report-field-formats-fn
                 :format-field-value format-field-value
                 :window-size-cell window-size
                 :top-y top-y
                 :graph-height graph-height
                 :summary-width summary-width})))
           ;; Graphs
           (with-time "render graphs"
             ;; Observe data through cell= and re-render graphs (e.g. on navigation ...->Edit Report->Report Result)
             (cell= (graphs (:rollup-rows report-data)
                            graph-height))))))))))

;;; Page-wide UI

(defelem pending
  "Returns UI for a message and a spinner."
  [attrs content]
  (div :class "pending"
       attrs
       (img :src "img/default/gears.gif")
       (div :class "pending-message"
            content)))

(defelem index
  [_ _]
  (screen/with-local-screen
    :region         "reports"
    :zone           "report"

    (if version/new-version?
      (component/app
       (cond-tpl
        (cell= (= :checking-status state))
        (pending (rpc/from-lang-pack ::index-state-checking-status))

        (cell= (some? error))
        (div
         :class "report-error"
         (h1  (rpc/from-lang-pack (cell= (or (:heading error) ::event-error-header-error))))
         (div
          (rpc/from-lang-pack (cell= (or (:heading-text error) ::index-state-error-main))))
         (h2 (rpc/from-lang-pack ::index-state-error-additional-details))
         (div (cell= (:detail error)))
         (h2 (rpc/from-lang-pack ::index-state-error-remediation-headline))
         (let [remediation (cell= (:remediation error))]
           (if-tpl remediation
                   (div remediation)
                   (div
                    ;; FIXME: This is not i18n-friendly in that it assumes
                    ;; English-like sentence structure where the email address is the
                    ;; last thing and is followed by a period.
                    ;;
                    ;; It would be nice if we could pass in the email address as a
                    ;; template value, but if we do that, then we can't make it a
                    ;; link.
                    (rpc/from-lang-pack ::index-state-error-remediation-none)
                    (a :href "mailto:support@kevel.co" "support@kevel.co")
                    "."))))

        (cell= (#{:edit-request :report-submitting} state))
        (new-report-form)

        (cell= (#{:edit-request :scheduled-report-editing} state))
        (new-report-form)

        (cell= (#{:awaiting-report :downloading-meta} state))
        (pending
         (let [status (cell= (get report-status "status"))]
           (cond
             (= status const/REPORT-STATUS-SUBMITTED)   (rpc/from-lang-pack ::index-report-state-submitted)
             (= status const/REPORT-STATUS-DONE)        (rpc/from-lang-pack ::index-report-state-done)
             (= status const/REPORT-STATUS-IN-PROGRESS) (rpc/from-lang-pack ::index-report-state-in-progress)
             :else                                      (rpc/from-lang-pack ::index-report-state-else))))

        (cell= (= :computing-in-worker state))
        (pending (rpc/from-lang-pack ::index-state-computing-in-worker))

        ;; TODO answer for error handling
        ;; (cell= (= :submit-error state))
        ;; (vector
        ;;  (div "There was an error submitting the report. "
        ;;       (a :href "#!/reports/new" "Click here to try again.")))

        (cell= (= :displaying-report state))
        (existing-report)

        (cell= (= :report-too-big state))
        (existing-report {:report-too-big true}))
       (dev/pre-cell "State" state)
       (dev/pre-cell "Error" error)
       (dev/pre-cell "Report status" report-status)
       (dev/pre-cell "Report-data" report-data)
       (dev/pre-cell "User" rpc/user))
      (component/app
       (cond-tpl
        (cell= (= :checking-status state))
        (pending (rpc/from-lang-pack ::index-state-checking-status))

        (cell= (some? error))
        (div
         :class "report-error"
         (h1  (rpc/from-lang-pack (cell= (or (:heading error) ::event-error-header-error))))
         (div
          (rpc/from-lang-pack (cell= (or (:heading-text error) ::index-state-error-main))))
         (h2 (rpc/from-lang-pack ::index-state-error-additional-details))
         (div (cell= (:detail error)))
         (h2 (rpc/from-lang-pack ::index-state-error-remediation-headline))
         (let [remediation (cell= (:remediation error))]
           (if-tpl remediation
                   (div remediation)
                   (div
                    ;; FIXME: This is not i18n-friendly in that it assumes
                    ;; English-like sentence structure where the email address is the
                    ;; last thing and is followed by a period.
                    ;;
                    ;; It would be nice if we could pass in the email address as a
                    ;; template value, but if we do that, then we can't make it a
                    ;; link.
                    (rpc/from-lang-pack ::index-state-error-remediation-none)
                    (a :href "mailto:support@kevel.co" "support@kevel.co")
                    "."))))

        (cell= (#{:edit-request :report-submitting} state))
        (new-report-form)

        (cell= (#{:edit-request :scheduled-report-editing} state))
        (new-report-form)

        (cell= (#{:awaiting-report :downloading-meta} state))
        (pending
         (let [status (cell= (get report-status "status"))]
           (cond
             (= status const/REPORT-STATUS-SUBMITTED)   (rpc/from-lang-pack ::index-report-state-submitted)
             (= status const/REPORT-STATUS-DONE)        (rpc/from-lang-pack ::index-report-state-done)
             (= status const/REPORT-STATUS-IN-PROGRESS) (rpc/from-lang-pack ::index-report-state-in-progress)
             :else                                      (rpc/from-lang-pack ::index-report-state-else))))

        (cell= (= :computing-in-worker state))
        (pending (rpc/from-lang-pack ::index-state-computing-in-worker))

        ;; TODO answer for error handling
        ;; (cell= (= :submit-error state))
        ;; (vector
        ;;  (div "There was an error submitting the report. "
        ;;       (a :href "#!/reports/new" "Click here to try again.")))

        (cell= (= :displaying-report state))
        (existing-report)

        (cell= (= :report-too-big state))
        (existing-report {:report-too-big true}))
       (dev/pre-cell "State" state)
       (dev/pre-cell "Error" error)
       (dev/pre-cell "Report status" report-status)
       (dev/pre-cell "Report-data" report-data)
       (dev/pre-cell "User" rpc/user)))))
